import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Calculator, AlertTriangle, X } from 'lucide-react';

function CreateWorktop() {
  const navigate = useNavigate();
  const { token, API, user } = useContext(AuthContext);
  const [basePostcode, setBasePostcode] = useState('');
  useEffect(() => {
    // Delivery distance is measured from the company's own base, not a hardcoded postcode
    const cid = user?.company_id;
    if (!cid) return;
    axios.get(`${API}/companies/${cid}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        const c = r.data || {};
        const m = `${c.postcode || ''} ${c.address || ''}`.match(/[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}/i);
        setBasePostcode((c.postcode || (m && m[0]) || '').toUpperCase());
      })
      .catch(() => {});
  }, [user?.company_id]);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [tradeUsers, setTradeUsers] = useState([]);
  const [customerType, setCustomerType] = useState('new'); // 'new' or 'trade'
  const [selectedTradeUser, setSelectedTradeUser] = useState('');
  
  // Price override state
  const [priceOverride, setPriceOverride] = useState({
    enabled: false,
    total: ''
  });
  
  // Hearth-specific state
  const [hearthShape, setHearthShape] = useState('Rectangle');
  const [deliveryMethod, setDeliveryMethod] = useState('collection');
  const [deliveryMileage, setDeliveryMileage] = useState('');
  const [deliveryCharge, setDeliveryCharge] = useState('');
  const [sameCutouts, setSameCutouts] = useState(true);
  const [leftCutout, setLeftCutout] = useState({ width: '', depth: '' });
  const [rightCutout, setRightCutout] = useState({ width: '', depth: '' });
  const [hasSlips, setHasSlips] = useState(false);
  const [slipHeight, setSlipHeight] = useState(50);
  
  // Groove calculator state
  const [grooveCalcResult, setGrooveCalcResult] = useState(null);
  const [calculatingGrooves, setCalculatingGrooves] = useState(false);
  
  const [formData, setFormData] = useState({
    order_type: 'Full Kitchen',
    display_name: '',  // Nickname/reference for the job
    client_name: '',
    client_email: '',
    client_phone: '',
    client_address: '',
    // Stone details (shared across all pieces)
    supplier: '',
    colour_name: '',
    pm2_cost: '',
    total_linear_metres: 0,
    vat_rate: 20,
    trade_discount: 0,
    deposit: 0,
    notes: '',
    // Extras
    undermount_sink: false,
    undermount_sink_qty: 1,
    undermount_sink_width: '',
    undermount_sink_depth: '',
    undermount_sink_discount: '',
    overmount_sink: false,
    overmount_sink_qty: 1,
    overmount_sink_width: '',
    overmount_sink_depth: '',
    overmount_sink_discount: '',
    hob_cutout: false,
    hob_cutout_qty: 1,
    socket_cutout: false,
    socket_cutout_qty: 1,
    popup_socket: false,
    tap_hole: false,
    tap_hole_qty: 1,
    radius_corner: false,
    radius_corner_qty: 1,
    large_rounded_corner: false,
    large_rounded_corner_qty: 1,
    drainer_grooves: false,
    drainer_grooves_side: '',
    drainer_grooves_length: '',
    // Groove spacing calculator fields
    drainer_sink_depth_mm: '',
    drainer_blade_width_mm: '10',  // Default blade width
    drainer_groove_count: '',  // Empty = auto-suggest
    drainer_step_distance_mm: '',
    recessed_drainer: false,
    custom_cutout: false,
    custom_cutout_width: '',
    custom_cutout_height: '',
    custom_cutout_notes: '',
    // Custom extras from settings
    selected_custom_extras: [],  // Array of {id, name, cost, width?, height?}
    // Custom fabrication amount
    custom_fabrication_cost: 0,
    // Services
    include_templating: true,  // Auto-checked for Full Kitchen
    include_fitting: true,      // Auto-checked for Full Kitchen
    templating_cost_override: '',
    templating_qty: 1,
    fitting_cost_override: '',
    fitting_qty: 1,
    // Edge
    edge_profile: '',
    // Trade user ID (if creating for trade user)
    trade_user_id: '',
    // Surfacing & Stonemasonry fields
    job_description: '',
    material_cost_per_m2: '',
    number_of_units: ''  // For calculating price per unit
  });
  const [pieces, setPieces] = useState([{
    name: '',
    width_mm: '',
    height_mm: '',
    thickness: '20mm',
    edges: { top: false, right: false, bottom: false, left: false },
    is_mitred: false,
    face_polished: false,
    has_slips: false,
    slip_height: 50,
    quantity: 1
  }]);
  const [attachments, setAttachments] = useState([]);
  // Multi-section mode
  const [multiSection, setMultiSection] = useState(false);
  // New trade user inline form
  const [showNewTradeForm, setShowNewTradeForm] = useState(false);
  const [creatingTradeUser, setCreatingTradeUser] = useState(false);
  const [newTradeForm, setNewTradeForm] = useState({
    business_name: '', name: '', email: '', phone: '', trade_discount: '0', password: ''
  });
  const [sections, setSections] = useState([{
    id: crypto.randomUUID(),
    name: 'Section 1',
    supplier: '',
    colour_name: '',
    pm2_cost: '',
    pieces: [{
      name: '',
      width_mm: '',
      height_mm: '',
      thickness: '20mm',
      edges: { top: false, right: false, bottom: false, left: false },
      is_mitred: false,
      face_polished: false,
      has_slips: false,
      slip_height: 50,
      quantity: 1
    }],
    ve_options: []
  }]);
  // Surfacing surfaces
  const [surfaces, setSurfaces] = useState([{ name: '', length_mm: '', width_mm: '', work_type: '' }]);
  // Stonemasonry blocks
  const [stoneBlocks, setStoneBlocks] = useState([{ description: '', length_mm: '', width_mm: '', depth_mm: '', quantity: 1 }]);
  // Line items for both Surfacing & Stonemasonry
  const [lineItems, setLineItems] = useState([{ description: '', price: '', qty: 1 }]);
  const isAncillary = formData.order_type === 'Surfacing' || formData.order_type === 'Stonemasonry';
  const [calculatedPrice, setCalculatedPrice] = useState({
    subtotalBeforeDiscount: 0,
    discountAmount: 0,
    subtotal: 0,
    vatAmount: 0,
    total: 0
  });

  useEffect(() => {
    fetchSettings();
    fetchTradeUsers();
  }, []);

  useEffect(() => {
    if (settings) {
      calculatePrice();
    }
  }, [pieces, formData, settings, deliveryMethod, deliveryCharge, lineItems, sections, multiSection]);

  // Auto-calculate delivery charge based on mileage
  useEffect(() => {
    if (deliveryMethod === 'delivery' && deliveryMileage && settings) {
      const miles = parseFloat(deliveryMileage) || 0;
      const ratePerMile = settings.delivery_rate_per_mile || 1.50;
      const calculatedCharge = (miles * ratePerMile).toFixed(2);
      setDeliveryCharge(calculatedCharge);
    }
  }, [deliveryMileage, deliveryMethod, settings]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/worktops/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load settings');
    }
  };

  const fetchTradeUsers = async () => {
    try {
      const response = await axios.get(`${API}/users/trade`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter for approved trade users (or users without trade_status field - backward compatibility)
      setTradeUsers(response.data.filter(user => !user.trade_status || user.trade_status === 'approved'));
    } catch (error) {
      console.error('Failed to load trade users:', error);
    }
  };

  const handleTradeUserSelect = (userId) => {
    setSelectedTradeUser(userId);
    const user = tradeUsers.find(u => u.id === userId);
    if (user) {
      setFormData({
        ...formData,
        client_name: user.business_name || user.name,
        client_email: user.email,
        client_phone: '',
        client_address: user.address || '',
        trade_discount: user.trade_discount || 0,
        trade_user_id: user.id
      });
    }
  };

  const handleCreateTradeUser = async () => {
    if (!newTradeForm.business_name || !newTradeForm.name || !newTradeForm.email || !newTradeForm.password) {
      toast.error('Please fill in Business Name, Contact Name, Email and Password');
      return;
    }
    setCreatingTradeUser(true);
    try {
      const response = await axios.post(`${API}/users`, {
        email: newTradeForm.email,
        name: newTradeForm.name,
        password: newTradeForm.password,
        role: 'trade',
        business_name: newTradeForm.business_name,
        trade_discount: parseFloat(newTradeForm.trade_discount) || 0
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success(`Trade account created for ${newTradeForm.business_name}`);
      
      // Refresh trade users list and select the new one
      await fetchTradeUsers();
      const newUserId = response.data.id;
      if (newUserId) {
        handleTradeUserSelect(newUserId);
      }
      
      setShowNewTradeForm(false);
      setNewTradeForm({ business_name: '', name: '', email: '', phone: '', trade_discount: '0', password: '' });
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to create trade user');
      toast.error(msg);
    } finally {
      setCreatingTradeUser(false);
    }
  };

  const calculateLinearMetres = () => {
    let polishedMetres = 0;
    let mitredMetres = 0;
    
    pieces
      .filter(piece => piece.width_mm && piece.height_mm)
      .forEach(piece => {
        const widthM = parseFloat(piece.width_mm) / 1000;
        const heightM = parseFloat(piece.height_mm) / 1000;
        
        let pieceEdgeMetres = 0;
        if (piece.edges?.top) pieceEdgeMetres += widthM;
        if (piece.edges?.right) pieceEdgeMetres += heightM;
        if (piece.edges?.bottom) pieceEdgeMetres += widthM;
        if (piece.edges?.left) pieceEdgeMetres += heightM;
        
        // Split into mitred or polished based on piece setting
        if (piece.is_mitred) {
          mitredMetres += pieceEdgeMetres;
        } else {
          polishedMetres += pieceEdgeMetres;
        }
      });
    
    return { polishedMetres, mitredMetres, totalMetres: polishedMetres + mitredMetres };
  };

  const calculatePrice = () => {
    let subtotal = 0;

    if (isAncillary) {
      // For Surfacing/Stonemasonry: sum of line items × qty
      lineItems.forEach(item => {
        const price = parseFloat(item.price) || 0;
        const qty = parseInt(item.qty) || 1;
        subtotal += price * qty;
      });
      // Add custom fabrication cost
      const customFabCost = parseFloat(formData.custom_fabrication_cost) || 0;
      if (customFabCost > 0) subtotal += customFabCost;
    } else {
      // Calculate pieces cost - only count filled pieces, use shared PM² cost
      const pm2Cost = parseFloat(formData.pm2_cost) || 0;
    
      // Multi-section mode: sum all section costs. If a section has active_ve_id, use that VE's pricing (swaps material rate + edges + optionally CNC)
      if (multiSection && sections.length > 0) {
        sections.forEach(section => {
          const activeVe = section.active_ve_id ? (section.ve_options || []).find(v => v.id === section.active_ve_id) : null;
          const rate = activeVe ? (parseFloat(activeVe.pm2_cost) || 0) : (parseFloat(section.pm2_cost) || 0);
          const revertToBevel = activeVe && activeVe.remove_edge_profiles;
          const removeCnc = activeVe && activeVe.remove_cnc;
          const bevelCost = settings.edge_profiles?.['Bevel'] || 20;
          const mitredEdgeRate = parseFloat(settings.mitred_edge_cost) || 45;
          const facePolishRate = parseFloat(settings.face_polish_cost) || 0;
          section.pieces.filter(p => p.width_mm && p.height_mm).forEach(p => {
            const w = parseFloat(p.width_mm);
            const h = parseFloat(p.height_mm);
            const area = (w * h) / 1000000;
            const qty = parseInt(p.quantity) || 1;
            if (rate > 0) {
              subtotal += area * rate * settings.retail_multiplier * qty;
            }
            // Edge profile cost — mirrors backend (server.py ~12520-12570):
            // walk ticked L/R/F/B sides, bucket polished vs mitred per side
            // (mitred if mitred_sides[side] OR whole-piece is_mitred).
            // Legacy fallback: nothing ticked → width once at edge or mitre rate.
            if (p.edge_profile && settings.edge_profiles?.[p.edge_profile]) {
              const edgeRate = revertToBevel ? bevelCost : settings.edge_profiles[p.edge_profile];
              const edges = p.edges || {};
              const mitredSides = p.mitred_sides || {};
              const pieceIsMitred = !!p.is_mitred;
              const wM = w / 1000;
              const hM = h / 1000;
              let polishedLm = 0, mitredLm = 0, anyTicked = false;
              [['front', wM], ['back', wM], ['left', hM], ['right', hM]].forEach(([side, len]) => {
                if (!edges[side]) return;
                anyTicked = true;
                if (mitredSides[side] || pieceIsMitred) mitredLm += len;
                else polishedLm += len;
              });
              if (!anyTicked) {
                if (pieceIsMitred) mitredLm = wM;
                else polishedLm = wM;
              }
              subtotal += polishedLm * qty * edgeRate;
              subtotal += mitredLm * qty * mitredEdgeRate;
            }
            // Top/Bottom face polish — per m² per face ticked (or legacy face_polished).
            if (facePolishRate > 0) {
              const edges = p.edges || {};
              let faces = 0;
              if (edges.top) faces += 1;
              if (edges.bottom) faces += 1;
              if (!edges.top && p.face_polished) faces += 1;
              if (faces) subtotal += area * facePolishRate * faces * qty;
            }
          });
          if (!removeCnc) {
            const cncHours = parseFloat(section.cnc_hours) || 0;
            const cncRate = parseFloat(section.cnc_rate) || parseFloat(settings?.cnc_rate) || 65;
            subtotal += cncHours * cncRate;
          }
        });
      } else if (pm2Cost > 0) {
        // Single material mode
        pieces
          .filter(piece => piece.width_mm && piece.height_mm)
          .forEach(piece => {
            const width = parseFloat(piece.width_mm);
            const height = parseFloat(piece.height_mm);
            const qty = parseInt(piece.quantity) || 1;
          
            const areaM2 = (width * height) / 1000000;
            const pieceCost = areaM2 * pm2Cost * settings.retail_multiplier * qty;
            subtotal += pieceCost;
          });
      }

      // Add edge profile cost - only for single-material mode
      // Multi-section mode has per-piece edge profiles already included in section cost
      if (!multiSection && formData.edge_profile && settings.edge_profiles[formData.edge_profile]) {
        const { polishedMetres, mitredMetres } = calculateLinearMetres();
        const polishedCost = settings.edge_profiles[formData.edge_profile] * polishedMetres;
        const mitredCost = (settings.mitred_edge_cost || 45) * mitredMetres;
        subtotal += polishedCost + mitredCost;
      }

      // Add face polishing cost - calculated per m² of face-polished pieces (single mode only)
      const facePolishCostPerM2 = settings.face_polish_cost || 0;
      if (!multiSection && facePolishCostPerM2 > 0) {
        pieces
          .filter(piece => piece.width_mm && piece.height_mm && piece.face_polished)
          .forEach(piece => {
            const width = parseFloat(piece.width_mm);
            const height = parseFloat(piece.height_mm);
            const qty = parseInt(piece.quantity) || 1;
            const areaM2 = (width * height) / 1000000;
            subtotal += areaM2 * facePolishCostPerM2 * qty;
          });
      }

      // Add extras
      if (formData.undermount_sink) {
        const perUnit = Math.max(0, (settings.undermount_sink_cost || 0) - (parseFloat(formData.undermount_sink_discount) || 0));
        subtotal += perUnit * (parseInt(formData.undermount_sink_qty) || 1);
      }
      if (formData.overmount_sink) {
        const perUnit = Math.max(0, (settings.overmount_sink_cost || 0) - (parseFloat(formData.overmount_sink_discount) || 0));
        subtotal += perUnit * (parseInt(formData.overmount_sink_qty) || 1);
      }
      if (formData.hob_cutout) subtotal += (settings.hob_cutout_cost || 0) * (parseInt(formData.hob_cutout_qty) || 1);
      if (formData.socket_cutout) subtotal += (settings.socket_cutout_cost || 0) * (parseInt(formData.socket_cutout_qty) || 1);
      if (formData.popup_socket) subtotal += settings.popup_socket_cost || 0;
      if (formData.tap_hole) subtotal += (settings.tap_hole_cost || 0) * (parseInt(formData.tap_hole_qty) || 1);
      if (formData.radius_corner) subtotal += (settings.radius_corner_cost || 0) * (parseInt(formData.radius_corner_qty) || 1);
      if (formData.large_rounded_corner) subtotal += (settings.large_rounded_corner_cost || 0) * (parseInt(formData.large_rounded_corner_qty) || 1);
      if (formData.drainer_grooves) subtotal += settings.drainer_grooves_cost || 0;
      if (formData.recessed_drainer) subtotal += settings.recessed_drainer_cost || 0;
      if (formData.custom_cutout && formData.custom_cutout_width && formData.custom_cutout_height) {
        const cutting_length_m = (parseFloat(formData.custom_cutout_width) + parseFloat(formData.custom_cutout_height)) / 1000;
        const cutout_cost = cutting_length_m * (settings.custom_cutout_cost_per_m || 25);
        subtotal += cutout_cost;
      }
    
      // Add selected custom extras
      if (formData.selected_custom_extras && formData.selected_custom_extras.length > 0) {
        formData.selected_custom_extras.forEach(extra => {
          subtotal += extra.cost || 0;
        });
      }
    
      // Add custom fabrication cost if entered
      const customFabCost = parseFloat(formData.custom_fabrication_cost) || 0;
      if (customFabCost > 0) {
        subtotal += customFabCost;
      }

      // Add services
      if (formData.include_templating) {
        const tCost = formData.templating_cost_override !== '' ? parseFloat(formData.templating_cost_override) : (settings.templating_cost || 0);
        subtotal += (tCost || 0) * (parseInt(formData.templating_qty) || 1);
      }
      if (formData.include_fitting) {
        const fCost = formData.fitting_cost_override !== '' ? parseFloat(formData.fitting_cost_override) : (settings.fitting_cost || 0);
        subtotal += (fCost || 0) * (parseInt(formData.fitting_qty) || 1);
      }

      // Add delivery charge
      if (deliveryMethod === 'delivery' && deliveryCharge) {
        subtotal += parseFloat(deliveryCharge) || 0;
      }
    }

    // Apply trade discount
    const subtotalBeforeDiscount = subtotal;
    const discount = parseFloat(formData.trade_discount) || 0;
    let discountAmount = 0;
    if (discount > 0) {
      discountAmount = subtotalBeforeDiscount * (discount / 100);
      subtotal = subtotalBeforeDiscount - discountAmount;
    }

    // Calculate VAT
    const vatRate = parseFloat(formData.vat_rate) || 0;
    const vatAmount = subtotal * (vatRate / 100);
    const total = subtotal + vatAmount;

    setCalculatedPrice({
      subtotalBeforeDiscount: subtotalBeforeDiscount,
      discountAmount: discountAmount,
      subtotal: subtotal,
      vatAmount: vatAmount,
      total: total
    });
  };

  const addPiece = () => {
    setPieces([...pieces, {
      name: '',
      width_mm: '',
      height_mm: '',
      thickness: '20mm',
      edges: { top: false, right: false, bottom: false, left: false },
      is_mitred: false,
      face_polished: false,
      has_slips: false,
      slip_height: 50,
      quantity: 1
    }]);
  };

  const removePiece = (index) => {
    setPieces(pieces.filter((_, i) => i !== index));
  };

  const duplicatePiece = (index) => {
    const pieceToDuplicate = pieces[index];
    const duplicated = {
      ...pieceToDuplicate,
      name: pieceToDuplicate.name ? `${pieceToDuplicate.name} (Copy)` : ''
    };
    const newPieces = [...pieces];
    newPieces.splice(index + 1, 0, duplicated);
    setPieces(newPieces);
  };

  // ===== Multi-Section Functions =====
  const addSection = () => {
    setSections([...sections, {
      id: crypto.randomUUID(),
      name: `Section ${sections.length + 1}`,
      supplier: '',
      colour_name: '',
      pm2_cost: '',
      pieces: [{
        name: '', width_mm: '', height_mm: '', thickness: '20mm',
        edges: { top: false, right: false, bottom: false, left: false, front: false, back: false },
        edge_profile: '',
        is_mitred: false, face_polished: false, has_slips: false, slip_height: 50, quantity: 1
      }],
      ve_options: []
    }]);
  };

  const removeSection = (sectionIndex) => {
    if (sections.length <= 1) return;
    setSections(sections.filter((_, i) => i !== sectionIndex));
  };

  const updateSection = (sectionIndex, field, value) => {
    const updated = [...sections];
    updated[sectionIndex] = { ...updated[sectionIndex], [field]: value };
    setSections(updated);
  };

  const addSectionPiece = (sectionIndex) => {
    const updated = [...sections];
    updated[sectionIndex].pieces.push({
      name: '', width_mm: '', height_mm: '', thickness: '20mm',
      edges: { top: false, right: false, bottom: false, left: false, front: false, back: false },
      edge_profile: '',
      is_mitred: false, face_polished: false, has_slips: false, slip_height: 50, quantity: 1
    });
    setSections(updated);
  };

  const removeSectionPiece = (sectionIndex, pieceIndex) => {
    const updated = [...sections];
    updated[sectionIndex].pieces = updated[sectionIndex].pieces.filter((_, i) => i !== pieceIndex);
    setSections(updated);
  };

  const updateSectionPiece = (sectionIndex, pieceIndex, field, value) => {
    const updated = [...sections];
    updated[sectionIndex].pieces[pieceIndex] = { ...updated[sectionIndex].pieces[pieceIndex], [field]: value };
    
    // Auto-add new piece if last piece has width and height filled
    const sectionPieces = updated[sectionIndex].pieces;
    const lastPiece = sectionPieces[sectionPieces.length - 1];
    if (lastPiece.width_mm && lastPiece.height_mm && pieceIndex === sectionPieces.length - 1) {
      updated[sectionIndex].pieces.push({
        name: '', width_mm: '', height_mm: '', thickness: '20mm',
        edges: { top: false, right: false, bottom: false, left: false, front: false, back: false },
        is_mitred: false, face_polished: false, has_slips: false, slip_height: 50, quantity: 1
      });
    }
    
    setSections(updated);
  };

  const addVeOption = (sectionIndex) => {
    const updated = [...sections];
    updated[sectionIndex].ve_options.push({
      id: crypto.randomUUID(),
      supplier: '',
      colour_name: '',
      pm2_cost: ''
    });
    setSections(updated);
  };

  const removeVeOption = (sectionIndex, veIndex) => {
    const updated = [...sections];
    updated[sectionIndex].ve_options = updated[sectionIndex].ve_options.filter((_, i) => i !== veIndex);
    setSections(updated);
  };

  const updateVeOption = (sectionIndex, veIndex, field, value) => {
    const updated = [...sections];
    updated[sectionIndex].ve_options[veIndex] = { ...updated[sectionIndex].ve_options[veIndex], [field]: value };
    setSections(updated);
  };

  const toggleActiveVe = (sectionIndex, veId) => {
    const updated = [...sections];
    // Toggle: if currently active, clear it (back to Spec); else set this VE as active
    updated[sectionIndex].active_ve_id = updated[sectionIndex].active_ve_id === veId ? null : veId;
    setSections(updated);
  };

  const calculateSectionCost = (section) => {
    if (!settings) return 0;
    const pm2 = parseFloat(section.pm2_cost) || 0;
    let cost = 0;
    section.pieces.filter(p => p.width_mm && p.height_mm).forEach(p => {
      const area = (parseFloat(p.width_mm) * parseFloat(p.height_mm)) / 1000000;
      const qty = parseInt(p.quantity) || 1;
      cost += area * pm2 * settings.retail_multiplier * qty;
      // Edge profile cost per piece (linear metre based on width)
      if (p.edge_profile && settings.edge_profiles?.[p.edge_profile]) {
        const lm = parseFloat(p.width_mm) / 1000 * qty;
        cost += lm * settings.edge_profiles[p.edge_profile];
      }
    });
    // Add CNC cost
    const cncHours = parseFloat(section.cnc_hours) || 0;
    const cncRate = parseFloat(section.cnc_rate) || parseFloat(settings?.cnc_rate) || 65;
    cost += cncHours * cncRate;
    return cost;
  };

  const calculateVeCost = (section, veOption) => {
    if (!settings) return 0;
    const pm2 = parseFloat(veOption.pm2_cost) || 0;
    const bevelCost = settings.edge_profiles?.['Bevel'] || 20;
    let cost = 0;
    section.pieces.filter(p => p.width_mm && p.height_mm).forEach(p => {
      const area = (parseFloat(p.width_mm) * parseFloat(p.height_mm)) / 1000000;
      const qty = parseInt(p.quantity) || 1;
      cost += area * pm2 * settings.retail_multiplier * qty;
      // Edge profiling: revert to Bevel if "remove" checked, otherwise keep original
      if (p.edge_profile && settings.edge_profiles?.[p.edge_profile]) {
        const lm = parseFloat(p.width_mm) / 1000 * qty;
        if (veOption.remove_edge_profiles) {
          cost += lm * bevelCost;
        } else {
          cost += lm * settings.edge_profiles[p.edge_profile];
        }
      }
    });
    if (!veOption.remove_cnc) {
      const cncHours = parseFloat(section.cnc_hours) || 0;
      const cncRate = parseFloat(section.cnc_rate) || parseFloat(settings?.cnc_rate) || 65;
      cost += cncHours * cncRate;
    }
    return cost;
  };

  const updatePiece = (index, field, value) => {
    const updated = [...pieces];
    updated[index][field] = value;
    setPieces(updated);
    
    // Auto-add new piece if last piece has width and height filled
    const lastPiece = updated[updated.length - 1];
    const isLastPieceReady = lastPiece.width_mm && lastPiece.height_mm;
    
    if (isLastPieceReady && index === updated.length - 1) {
      setPieces([...updated, {
        name: '',
        width_mm: '',
        height_mm: '',
        thickness: '20mm',
        edges: { top: false, right: false, bottom: false, left: false },
        is_mitred: false,
        face_polished: false,
        has_slips: false,
        slip_height: 50,
        quantity: 1
      }]);
    }
  };

  // Calculate drainer groove spacing
  const calculateGrooveSpacing = async () => {
    const sinkDepth = parseFloat(formData.drainer_sink_depth_mm);
    const bladeWidth = parseFloat(formData.drainer_blade_width_mm);
    
    if (!sinkDepth || sinkDepth <= 0) {
      toast.error('Please enter a valid sink depth');
      return;
    }
    
    if (!bladeWidth || bladeWidth <= 0) {
      toast.error('Please enter a valid blade width');
      return;
    }
    
    try {
      setCalculatingGrooves(true);
      const response = await axios.post(`${API}/worktops/calculate-groove-spacing`, {
        sink_depth_mm: sinkDepth,
        blade_width_mm: bladeWidth,
        number_of_grooves: formData.drainer_groove_count ? parseInt(formData.drainer_groove_count) : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setGrooveCalcResult(response.data);
      
      // Update form data with calculated values
      setFormData(prev => ({
        ...prev,
        drainer_groove_count: response.data.number_of_grooves,
        drainer_step_distance_mm: response.data.step_distance_mm
      }));
      
      toast.success(`Calculated: ${response.data.number_of_grooves} grooves, ${response.data.step_distance_mm}mm step distance`);
    } catch (error) {
      toast.error('Failed to calculate groove spacing');
      console.error(error);
    } finally {
      setCalculatingGrooves(false);
    }
  };

  const handleSubmit = async (e, isDraft = false) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Handle Hearth Orders
      if (formData.order_type === 'Fireplace/Hearth') {
        const validPieces = pieces
          .filter(p => p.width_mm && p.height_mm)
          .map(p => ({
            name: p.name || 'Hearth',
            width_mm: parseFloat(p.width_mm) || 0,
            height_mm: parseFloat(p.height_mm) || 0,
            thickness: '20mm',
            edges: { top: true, right: true, bottom: true, left: true },
            is_mitred: p.is_mitred || false,
            face_polished: p.face_polished || false,
            supplier: formData.supplier,
            colour_name: formData.colour_name,
            pm2_cost: parseFloat(formData.pm2_cost) || 0,
            edge_profile: 'Polished',
            has_slips: p.has_slips || false,
            slip_height: p.has_slips ? (parseFloat(p.slip_height) || 50) : 0,
            quantity: parseInt(p.quantity) || 1
          }));

        // Allow no pieces if there's custom fabrication work
        const hasCustomFabrication = parseFloat(formData.custom_fabrication_cost) > 0;
        if (validPieces.length === 0 && !hasCustomFabrication) {
          toast.error('Please add at least one hearth piece or enter a custom fabrication cost');
          setLoading(false);
          return;
        }

        // Build cutout info
        let cutoutInfo = '';
        if (hearthShape === 'T-Shape (Single Piece)' && leftCutout.width && leftCutout.depth) {
          const leftInfo = `Left cut-out: ${leftCutout.width}mm x ${leftCutout.depth}mm`;
          const rightInfo = sameCutouts 
            ? 'Right cut-out: Same as left' 
            : `Right cut-out: ${rightCutout.width}mm x ${rightCutout.depth}mm`;
          cutoutInfo = `\n\n*** CUTOUTS ***\n${leftInfo}\n${rightInfo}\n`;
        }

        const deliveryChargeValue = deliveryMethod === 'delivery' && deliveryCharge ? parseFloat(deliveryCharge) : 0;
        const deliveryInfo = deliveryMethod === 'delivery' 
          ? `\nDelivery: GBP${deliveryChargeValue.toFixed(2)}` 
          : '\nCustomer Collection';

        // Calculate slips cost from pieces
        let totalSlipsCost = 0;
        let slipsInfo = '';
        const costPerM2 = parseFloat(formData.pm2_cost) || 0;
        
        validPieces.forEach((piece, idx) => {
          if (piece.has_slips) {
            const width = parseFloat(piece.width_mm) || 0;
            const depth = parseFloat(piece.height_mm) || 0;
            const slipH = parseFloat(piece.slip_height) || 50;
            
            // 2 short pieces (sides: depth - 20mm each) + 1 long piece (front: full width)
            const shortDepth = depth - 20;
            const fullWidth = width;
            const slipArea = (2 * shortDepth * slipH + fullWidth * slipH) / 1000000;
            const slipCost = slipArea * costPerM2;
            
            totalSlipsCost += slipCost;
            slipsInfo += `\nPiece ${idx + 1} Slips: 2x${shortDepth}mm x ${slipH}mm (sides) + 1x${fullWidth}mm x ${slipH}mm (front) = ${slipArea.toFixed(2)}m2 = GBP${slipCost.toFixed(2)}`;
          }
        });
        
        if (totalSlipsCost > 0) {
          slipsInfo += `\nTotal Slips Cost: GBP${totalSlipsCost.toFixed(2)}`;
        }

        const hearthPayload = {
          order_type: formData.order_type,
          display_name: formData.display_name || '',
          client_name: formData.client_name,
          client_email: formData.client_email,
          client_phone: formData.client_phone || '',
          client_address: formData.client_address || '',
          supplier: formData.supplier,
          colour_name: formData.colour_name,
          trade_user_id: formData.trade_user_id || '',
          pieces: validPieces,
          hearth_shape: hearthShape,
          slips_cost: totalSlipsCost,
          delivery_method: deliveryMethod,
          delivery_mileage: parseFloat(deliveryMileage) || 0,
          delivery_charge: deliveryChargeValue,
          vat_rate: parseFloat(formData.vat_rate) || 20,
          trade_discount: parseFloat(formData.trade_discount) || 0,
          deposit: parseFloat(formData.deposit) || 0,
          pm2_cost: parseFloat(formData.pm2_cost) || 0,
          total_linear_metres: 0,
          custom_fabrication_cost: parseFloat(formData.custom_fabrication_cost) || 0,
          undermount_sink: formData.undermount_sink || false,
          undermount_sink_qty: parseInt(formData.undermount_sink_qty) || 1,
          undermount_sink_width: parseFloat(formData.undermount_sink_width) || null,
          undermount_sink_depth: parseFloat(formData.undermount_sink_depth) || null,
          undermount_sink_discount: parseFloat(formData.undermount_sink_discount) || 0,
          overmount_sink: formData.overmount_sink || false,
          overmount_sink_qty: parseInt(formData.overmount_sink_qty) || 1,
          overmount_sink_width: parseFloat(formData.overmount_sink_width) || null,
          overmount_sink_depth: parseFloat(formData.overmount_sink_depth) || null,
          overmount_sink_discount: parseFloat(formData.overmount_sink_discount) || 0,
          hob_cutout: formData.hob_cutout || false,
          hob_cutout_qty: parseInt(formData.hob_cutout_qty) || 1,
          socket_cutout: formData.socket_cutout || false,
          socket_cutout_qty: parseInt(formData.socket_cutout_qty) || 1,
          popup_socket: formData.popup_socket || false,
          tap_hole: formData.tap_hole || false,
          tap_hole_qty: parseInt(formData.tap_hole_qty) || 1,
          radius_corner: formData.radius_corner || false,
          radius_corner_qty: parseInt(formData.radius_corner_qty) || 1,
          large_rounded_corner: formData.large_rounded_corner || false,
          large_rounded_corner_qty: parseInt(formData.large_rounded_corner_qty) || 1,
          drainer_grooves: formData.drainer_grooves || false,
          drainer_grooves_side: formData.drainer_grooves ? formData.drainer_grooves_side : null,
          drainer_grooves_length: formData.drainer_grooves && formData.drainer_grooves_length ? parseFloat(formData.drainer_grooves_length) : null,
          recessed_drainer: formData.recessed_drainer || false,
          custom_cutout: formData.custom_cutout || false,
          custom_cutout_width: formData.custom_cutout && formData.custom_cutout_width ? parseFloat(formData.custom_cutout_width) : 0,
          custom_cutout_height: formData.custom_cutout && formData.custom_cutout_height ? parseFloat(formData.custom_cutout_height) : 0,
          custom_cutout_notes: formData.custom_cutout_notes || '',
          selected_custom_extras: formData.selected_custom_extras || [],
          include_templating: formData.include_templating || false,
          include_fitting: formData.include_fitting || false,
          edge_profile: formData.edge_profile || '',
          notes: formData.notes + cutoutInfo + slipsInfo + `\n${deliveryInfo}`,
          status: isDraft ? 'draft' : 'quote'
        };

        const response = await axios.post(`${API}/worktops`, hearthPayload, {
          headers: { Authorization: `Bearer ${token}` }
        });

        toast.success('Hearth order created successfully');
        navigate(`/worktops/${response.data.id}`);
        return;
      }

      // Handle Surfacing/Stonemasonry Orders
      if (isAncillary) {
        const validLineItems = lineItems.filter(item => item.description && item.price);
        if (validLineItems.length === 0) {
          toast.error('Please add at least one line item');
          setLoading(false);
          return;
        }

        const ancillaryPayload = {
          order_type: formData.order_type,
          display_name: formData.display_name || '',
          client_name: formData.client_name,
          client_email: formData.client_email,
          client_phone: formData.client_phone || '',
          client_address: formData.client_address || '',
          job_description: formData.job_description || '',
          material_cost_per_m2: parseFloat(formData.material_cost_per_m2) || 0,
          surfaces: formData.order_type === 'Surfacing' ? surfaces.filter(s => s.name || s.length_mm || s.width_mm) : [],
          stone_blocks: formData.order_type === 'Stonemasonry' ? stoneBlocks.filter(b => b.description || b.length_mm) : [],
          line_items: validLineItems.map(item => ({ 
            description: item.description, 
            price: parseFloat(item.price) || 0,
            qty: parseInt(item.qty) || 1
          })),
          pieces: [],
          total_linear_metres: 0,
          custom_fabrication_cost: parseFloat(formData.custom_fabrication_cost) || 0,
          number_of_units: parseInt(formData.number_of_units) || 0,
          vat_rate: parseFloat(formData.vat_rate) || 20,
          trade_discount: parseFloat(formData.trade_discount) || 0,
          deposit: parseFloat(formData.deposit) || 0,
          notes: formData.notes || '',
          attachments: attachments,
          status: isDraft ? 'draft' : 'new_quote',
          price_override_enabled: priceOverride.enabled,
          price_override_total: priceOverride.enabled ? parseFloat(priceOverride.total) || 0 : null
        };

        const response = await axios.post(`${API}/worktops`, ancillaryPayload, {
          headers: { Authorization: `Bearer ${token}` }
        });

        toast.success(`${formData.order_type} quote created successfully`);
        navigate(`/worktops/${response.data.id}`);
        return;
      }

      // Handle Worktop Orders (existing code)
      const validPieces = pieces
        .filter(p => p.width_mm && p.height_mm)
        .map(p => ({
          name: p.name || '',
          width_mm: parseFloat(p.width_mm) || 0,
          height_mm: parseFloat(p.height_mm) || 0,
          thickness: p.thickness,
          edges: p.edges || { top: false, right: false, bottom: false, left: false },
          is_mitred: p.is_mitred || false,
          face_polished: p.face_polished || false,
          supplier: formData.supplier,
          colour_name: formData.colour_name,
          pm2_cost: parseFloat(formData.pm2_cost) || 0,
          quantity: parseInt(p.quantity) || 1
        }));
      
      // Allow no pieces if there's custom fabrication work
      const hasCustomFabrication = parseFloat(formData.custom_fabrication_cost) > 0;
      const hasSectionPieces = multiSection && sections.some(s => s.pieces.some(p => p.width_mm && p.height_mm));
      if (validPieces.length === 0 && !hasCustomFabrication && !hasSectionPieces) {
        toast.error('Please add at least one stone piece or enter a custom fabrication cost');
        setLoading(false);
        return;
      }

      const payload = {
        ...formData,
        pieces: validPieces,
        total_linear_metres: calculateLinearMetres().totalMetres,
        vat_rate: parseFloat(formData.vat_rate) || 0,
        trade_discount: parseFloat(formData.trade_discount) || 0,
        deposit: parseFloat(formData.deposit) || 0,
        pm2_cost: parseFloat(formData.pm2_cost) || 0,
        custom_fabrication_cost: parseFloat(formData.custom_fabrication_cost) || 0,
        socket_cutout_qty: parseInt(formData.socket_cutout_qty) || 1,
        radius_corner_qty: parseInt(formData.radius_corner_qty) || 1,
        large_rounded_corner_qty: parseInt(formData.large_rounded_corner_qty) || 1,
        drainer_grooves_side: formData.drainer_grooves ? formData.drainer_grooves_side : null,
        drainer_grooves_length: formData.drainer_grooves && formData.drainer_grooves_length ? parseFloat(formData.drainer_grooves_length) : null,
        custom_cutout_width: formData.custom_cutout && formData.custom_cutout_width ? parseFloat(formData.custom_cutout_width) : 0,
        custom_cutout_height: formData.custom_cutout && formData.custom_cutout_height ? parseFloat(formData.custom_cutout_height) : 0,
        attachments: attachments,
        status: isDraft ? 'draft' : 'new_quote',
        // Price override
        price_override_enabled: priceOverride.enabled,
        price_override_total: priceOverride.enabled ? parseFloat(priceOverride.total) || 0 : null,
        // Multi-section data
        multi_section: multiSection,
        sections: multiSection ? sections.map(s => ({
          id: s.id,
          name: s.name,
          supplier: s.supplier,
          colour_name: s.colour_name,
          pm2_cost: parseFloat(s.pm2_cost) || 0,
          cnc_hours: parseFloat(s.cnc_hours) || 0,
          cnc_rate: parseFloat(s.cnc_rate) || parseFloat(settings?.cnc_rate) || 65,
          active_ve_id: s.active_ve_id || null,
          pieces: s.pieces.filter(p => p.width_mm && p.height_mm).map(p => ({
            name: p.name || '',
            width_mm: parseFloat(p.width_mm) || 0,
            height_mm: parseFloat(p.height_mm) || 0,
            thickness: p.thickness,
            edges: p.edges || { top: false, right: false, bottom: false, left: false },
            edge_profile: p.edge_profile || '',
            is_mitred: p.is_mitred || false,
            face_polished: p.face_polished || false,
            quantity: parseInt(p.quantity) || 1
          })),
          ve_options: s.ve_options.map(v => ({
            id: v.id,
            supplier: v.supplier,
            colour_name: v.colour_name,
            pm2_cost: parseFloat(v.pm2_cost) || 0,
            remove_edge_profiles: v.remove_edge_profiles || false,
            remove_cnc: v.remove_cnc || false,
            remove_mitres: v.remove_mitres || false
          }))
        })) : []
      };

      const response = await axios.post(`${API}/worktops`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (isDraft) {
        toast.success('Quote saved as draft');
      } else {
        toast.success('Quote created successfully');
      }
      navigate(`/worktops/${response.data.id}`);
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Failed to create order';
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(err => err.msg || JSON.stringify(err)).join(', ');
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!settings) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 font-sans">
      <div className="max-w-4xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/worktops')}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Worktops
        </Button>

        <form onSubmit={handleSubmit}>
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Create Worktop Order / Quote</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Order Type */}
              <div>
                <Label>Order Type</Label>
                <select
                  value={formData.order_type}
                  onChange={(e) => {
                    const isFullKitchen = e.target.value === 'Full Kitchen';
                    setFormData({ 
                      ...formData, 
                      order_type: e.target.value,
                      include_templating: isFullKitchen,
                      include_fitting: isFullKitchen
                    });
                  }}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="Full Kitchen">Full Kitchen</option>
                  <option value="Offcut">Offcut</option>
                  <option value="Custom Fabrication">Custom Fabrication</option>
                  <option value="Fireplace/Hearth">Fireplace/Hearth</option>
                  <option value="Surfacing">Surfacing</option>
                  <option value="Stonemasonry">Stonemasonry</option>
                </select>
              </div>

              {/* Customer Type Selection */}
              <div className="border-t pt-4">
                <Label>Customer Type</Label>
                <select
                  value={customerType}
                  onChange={(e) => {
                    setCustomerType(e.target.value);
                    if (e.target.value === 'new') {
                      setSelectedTradeUser('');
                      setFormData({
                        ...formData,
                        client_name: '',
                        client_email: '',
                        client_phone: '',
                        client_address: '',
                        trade_discount: 0,
                        trade_user_id: ''
                      });
                    }
                  }}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="new">New Customer</option>
                  <option value="trade">Trade User</option>
                </select>
              </div>

              {/* Trade User Selection */}
              {customerType === 'trade' && (
                <div className="space-y-3">
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <Label>Select Trade User *</Label>
                      <select
                        value={selectedTradeUser}
                        onChange={(e) => handleTradeUserSelect(e.target.value)}
                        className="w-full px-3 py-2 border rounded-md"
                        required={customerType === 'trade' && !showNewTradeForm}
                      >
                        <option value="">-- Select a trade user --</option>
                        {tradeUsers.map(user => (
                          <option key={user.id} value={user.id}>
                            {user.business_name || user.name} ({user.email}) - {user.trade_discount}% discount
                          </option>
                        ))}
                      </select>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowNewTradeForm(!showNewTradeForm)}
                      className="text-blue-600 border-blue-300 hover:bg-blue-50 whitespace-nowrap"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      New Trade User
                    </Button>
                  </div>
                  {selectedTradeUser && !showNewTradeForm && (
                    <p className="text-sm text-green-600">
                      Order will be created for this trade user with {formData.trade_discount}% discount
                    </p>
                  )}
                  
                  {/* Quick Create Trade User Form */}
                  {showNewTradeForm && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                      <p className="text-sm font-semibold text-blue-800">Create New Trade Account</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Business Name *</Label>
                          <Input
                            value={newTradeForm.business_name}
                            onChange={(e) => setNewTradeForm({...newTradeForm, business_name: e.target.value})}
                            placeholder="e.g., Smith Kitchens Ltd"
                            className="h-8"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Contact Name *</Label>
                          <Input
                            value={newTradeForm.name}
                            onChange={(e) => setNewTradeForm({...newTradeForm, name: e.target.value})}
                            placeholder="e.g., John Smith"
                            className="h-8"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Email *</Label>
                          <Input
                            type="email"
                            value={newTradeForm.email}
                            onChange={(e) => setNewTradeForm({...newTradeForm, email: e.target.value})}
                            placeholder="john@smithkitchens.co.uk"
                            className="h-8"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Phone</Label>
                          <Input
                            value={newTradeForm.phone}
                            onChange={(e) => setNewTradeForm({...newTradeForm, phone: e.target.value})}
                            placeholder="07..."
                            className="h-8"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Trade Discount (%)</Label>
                          <Input
                            type="number"
                            min="0"
                            max="50"
                            value={newTradeForm.trade_discount}
                            onChange={(e) => setNewTradeForm({...newTradeForm, trade_discount: e.target.value})}
                            placeholder="0"
                            className="h-8"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Password *</Label>
                          <Input
                            type="password"
                            value={newTradeForm.password}
                            onChange={(e) => setNewTradeForm({...newTradeForm, password: e.target.value})}
                            placeholder="Set login password"
                            className="h-8"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2 pt-1">
                        <Button
                          type="button"
                          size="sm"
                          onClick={handleCreateTradeUser}
                          disabled={creatingTradeUser}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          {creatingTradeUser ? 'Creating...' : 'Create & Select'}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => setShowNewTradeForm(false)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Client Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>Job Nickname / Reference</Label>
                  <Input
                    value={formData.display_name}
                    onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                    placeholder="e.g., Smith Kitchen Remodel, 50 Showers Contract"
                  />
                  <p className="text-xs text-gray-500 mt-1">This will appear on quotes sent to clients</p>
                </div>
                <div>
                  <Label>Client Name *</Label>
                  <Input
                    value={formData.client_name}
                    onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                    required
                    readOnly={customerType === 'trade'}
                    className={customerType === 'trade' ? 'bg-gray-100' : ''}
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={formData.client_email}
                    onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                    required
                    readOnly={customerType === 'trade'}
                    className={customerType === 'trade' ? 'bg-gray-100' : ''}
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={formData.client_phone}
                    onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                    readOnly={customerType === 'trade'}
                    className={customerType === 'trade' ? 'bg-gray-100' : ''}
                  />
                </div>
                <div>
                  <Label>Address</Label>
                  <Input
                    value={formData.client_address}
                    onChange={(e) => setFormData({ ...formData, client_address: e.target.value })}
                    readOnly={customerType === 'trade'}
                    className={customerType === 'trade' ? 'bg-gray-100' : ''}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Conditional Rendering Based on Order Type */}
          {formData.order_type === 'Fireplace/Hearth' ? (
            <>
              {/* Hearth-Specific Fields */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>Hearth Shape</CardTitle>
                </CardHeader>
                <CardContent>
                  <Label>Select Shape *</Label>
                  <select
                    value={hearthShape}
                    onChange={(e) => setHearthShape(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md mt-2"
                  >
                    <option value="Rectangle">Rectangle</option>
                    <option value="T-Shape (Single Piece)">T-Shape (Single Piece)</option>
                    <option value="T-Shape (Two Pieces)">T-Shape (Two Pieces)</option>
                    <option value="Quadrant">Quadrant</option>
                    <option value="Custom Shape">Custom Shape</option>
                  </select>
                </CardContent>
              </Card>

              {/* Material for Hearth */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>Material</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label>Supplier</Label>
                      <Input
                        value={formData.supplier}
                        onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                        placeholder="Granite"
                      />
                    </div>
                    <div>
                      <Label>Material Name *</Label>
                      <Input
                        value={formData.colour_name}
                        onChange={(e) => setFormData({ ...formData, colour_name: e.target.value })}
                        required
                        placeholder="e.g., Honed Black Granite"
                      />
                    </div>
                    <div>
                      <Label>Cost per m² (£)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.pm2_cost}
                        onChange={(e) => setFormData({ ...formData, pm2_cost: e.target.value })}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Hearth Dimensions */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>Dimensions (20mm thickness standard)</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {pieces.map((piece, index) => (
                    <div key={index} className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold">{piece.name || `Piece ${index + 1}`}</h4>
                        {pieces.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removePiece(index)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      <div className="grid md:grid-cols-3 gap-4 mb-4">
                        <div>
                          <Label>Width (left to right) *</Label>
                          <Input
                            type="number"
                            value={piece.width_mm}
                            onChange={(e) => updatePiece(index, 'width_mm', e.target.value)}
                            placeholder="e.g. 1200"
                            required
                          />
                        </div>
                        <div>
                          <Label>Depth (front to back) *</Label>
                          <Input
                            type="number"
                            value={piece.height_mm}
                            onChange={(e) => updatePiece(index, 'height_mm', e.target.value)}
                            placeholder="e.g. 600"
                            required
                          />
                        </div>
                        <div>
                          <Label>Qty</Label>
                          <Input
                            type="number"
                            min="1"
                            value={piece.quantity ?? ''}
                            onChange={(e) => updatePiece(index, 'quantity', e.target.value === '' ? '' : parseInt(e.target.value))}
                            onBlur={(e) => {
                              if (!e.target.value || parseInt(e.target.value) < 1) {
                                updatePiece(index, 'quantity', 1);
                              }
                            }}
                            data-testid={`piece-qty-${index}`}
                          />
                        </div>
                      </div>

                      {/* Slips Option for this piece */}
                      <div className="border-t pt-3 mt-3">
                        <div className="flex items-center mb-3">
                          <input
                            type="checkbox"
                            id={`slips_${index}`}
                            checked={piece.has_slips || false}
                            onChange={(e) => updatePiece(index, 'has_slips', e.target.checked)}
                            className="w-4 h-4"
                          />
                          <Label htmlFor={`slips_${index}`} className="ml-2">Add Slips to this piece</Label>
                        </div>

                        {piece.has_slips && (
                          <div className="space-y-3 pl-6">
                            <div>
                              <Label>Slip Height (mm)</Label>
                              <Input
                                type="number"
                                value={piece.slip_height || 50}
                                onChange={(e) => updatePiece(index, 'slip_height', e.target.value)}
                              />
                            </div>

                            {piece.width_mm && piece.height_mm && formData.pm2_cost && (
                              <div className="p-3 bg-blue-50 border border-blue-200 rounded text-xs">
                                <p className="font-semibold text-gray-900 mb-2">Slip Calculation:</p>
                                <div className="space-y-1 text-gray-700">
                                  <p>• 2 short pieces (sides): {parseFloat(piece.height_mm) - 20}mm × {piece.slip_height || 50}mm each</p>
                                  <p>• 1 long piece (front): {parseFloat(piece.width_mm)}mm × {piece.slip_height || 50}mm</p>
                                  <p className="pt-2 border-t">
                                    Total Area: {
                                      (() => {
                                        const shortDepth = parseFloat(piece.height_mm) - 20;
                                        const fullWidth = parseFloat(piece.width_mm);
                                        const slipH = parseFloat(piece.slip_height) || 50;
                                        const area = (2 * shortDepth * slipH + fullWidth * slipH) / 1000000;
                                        return area.toFixed(3);
                                      })()
                                    }m²
                                  </p>
                                  <p className="font-bold text-gray-900">
                                    Slip Cost: £{
                                      (() => {
                                        const shortDepth = parseFloat(piece.height_mm) - 20;
                                        const fullWidth = parseFloat(piece.width_mm);
                                        const slipH = parseFloat(piece.slip_height) || 50;
                                        const area = (2 * shortDepth * slipH + fullWidth * slipH) / 1000000;
                                        const cost = area * parseFloat(formData.pm2_cost);
                                        return cost.toFixed(2);
                                      })()
                                    }
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {(hearthShape === 'T-Shape (Two Pieces)' || hearthShape === 'Custom Shape') && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={addPiece}
                      className="w-full border-dashed"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Another Piece
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* T-Shape Cut-Outs */}
              {hearthShape === 'T-Shape (Single Piece)' && (
                <Card className="mb-6 border-2 border-gray-800">
                  <CardHeader className="bg-gray-800 text-white">
                    <CardTitle>T-Shape Cut-Out Dimensions</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 pt-6">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="same_cutouts_admin"
                        checked={sameCutouts}
                        onChange={(e) => setSameCutouts(e.target.checked)}
                        className="w-4 h-4"
                      />
                      <Label htmlFor="same_cutouts_admin" className="ml-2">Both cut-outs are the same size</Label>
                    </div>

                    <div className="border rounded-lg p-4 bg-blue-50">
                      <h4 className="font-semibold mb-3">Left Cut-Out</h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Width (left to right)</Label>
                          <Input
                            type="number"
                            value={leftCutout.width}
                            onChange={(e) => setLeftCutout({ ...leftCutout, width: e.target.value })}
                            placeholder="mm"
                          />
                        </div>
                        <div>
                          <Label>Depth (front to back)</Label>
                          <Input
                            type="number"
                            value={leftCutout.depth}
                            onChange={(e) => setLeftCutout({ ...leftCutout, depth: e.target.value })}
                            placeholder="mm"
                          />
                        </div>
                      </div>
                    </div>

                    {!sameCutouts && (
                      <div className="border rounded-lg p-4 bg-green-50">
                        <h4 className="font-semibold mb-3">Right Cut-Out</h4>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <Label>Width (left to right)</Label>
                            <Input
                              type="number"
                              value={rightCutout.width}
                              onChange={(e) => setRightCutout({ ...rightCutout, width: e.target.value })}
                              placeholder="mm"
                            />
                          </div>
                          <div>
                            <Label>Depth (front to back)</Label>
                            <Input
                              type="number"
                              value={rightCutout.depth}
                              onChange={(e) => setRightCutout({ ...rightCutout, depth: e.target.value })}
                              placeholder="mm"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Delivery Options */}
              <Card className="mb-6">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="include-delivery"
                      checked={deliveryMethod === 'delivery'}
                      onChange={(e) => {
                        setDeliveryMethod(e.target.checked ? 'delivery' : 'collection');
                        if (!e.target.checked) {
                          setDeliveryMileage('');
                          setDeliveryCharge('');
                        }
                      }}
                      className="h-5 w-5 rounded border-gray-300"
                    />
                    <Label htmlFor="include-delivery" className="text-base font-semibold cursor-pointer">Include Delivery</Label>
                  </div>
                  
                  {deliveryMethod === 'delivery' && (
                    <div className="mt-4 pl-8 space-y-3 border-l-2 border-blue-200">
                      <div>
                        <Label>Distance (miles from {basePostcode || 'your base'})</Label>
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          value={deliveryMileage}
                          onChange={(e) => setDeliveryMileage(e.target.value)}
                          placeholder="e.g., 25"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Rate: £{settings?.delivery_rate_per_mile?.toFixed(2) || '1.50'} per mile
                        </p>
                      </div>
                      <div>
                        <Label>Delivery Charge (£)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={deliveryCharge}
                          onChange={(e) => setDeliveryCharge(e.target.value)}
                          placeholder="Auto-calculated or enter manually"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          {deliveryMileage ? `Calculated: ${deliveryMileage} miles × £${settings?.delivery_rate_per_mile?.toFixed(2) || '1.50'}` : 'Enter mileage to auto-calculate, or enter manually'}
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          ) : isAncillary ? (
            <>
              {/* Surfacing/Stonemasonry Fields */}
              <Card className="mb-6 border-l-4 border-amber-500">
                <CardHeader>
                  <CardTitle className="text-amber-900">Job Description</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Describe the overall job *</Label>
                    <textarea
                      value={formData.job_description}
                      onChange={(e) => setFormData({ ...formData, job_description: e.target.value })}
                      placeholder={formData.order_type === 'Surfacing' 
                        ? "e.g., Re-polish shower walls and restore slate tile surfaces..." 
                        : "e.g., Hand cut and replace portland stone wall copings, supply stone coverings..."}
                      className="w-full px-3 py-2 border rounded-md min-h-[100px] resize-y"
                      data-testid="job-description-input"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Material Cost per m² (to you)</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">£</span>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.material_cost_per_m2}
                          onChange={(e) => setFormData({ ...formData, material_cost_per_m2: e.target.value })}
                          placeholder="0.00"
                          className="pl-7"
                          data-testid="material-cost-per-m2-input"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Surfacing - Surface Items */}
              {formData.order_type === 'Surfacing' && (
                <Card className="mb-6 border-l-4 border-cyan-500">
                  <CardHeader>
                    <CardTitle className="text-cyan-900">Surfaces</CardTitle>
                    <p className="text-sm text-cyan-600">Dimensions for each surface to be worked on</p>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {surfaces.map((surface, idx) => (
                      <div key={idx} className="flex items-end gap-2 p-3 bg-cyan-50 rounded-lg">
                        <div className="flex-1">
                          <Label className="text-xs">Surface Name</Label>
                          <Input
                            value={surface.name}
                            onChange={(e) => {
                              const updated = [...surfaces];
                              updated[idx].name = e.target.value;
                              setSurfaces(updated);
                            }}
                            placeholder="e.g., Shower wall left"
                            data-testid={`surface-name-${idx}`}
                          />
                        </div>
                        <div className="w-28">
                          <Label className="text-xs">Length (mm)</Label>
                          <Input
                            type="number"
                            value={surface.length_mm}
                            onChange={(e) => {
                              const updated = [...surfaces];
                              updated[idx].length_mm = e.target.value;
                              setSurfaces(updated);
                            }}
                            placeholder="mm"
                            data-testid={`surface-length-${idx}`}
                          />
                        </div>
                        <div className="w-28">
                          <Label className="text-xs">Width (mm)</Label>
                          <Input
                            type="number"
                            value={surface.width_mm}
                            onChange={(e) => {
                              const updated = [...surfaces];
                              updated[idx].width_mm = e.target.value;
                              setSurfaces(updated);
                            }}
                            placeholder="mm"
                            data-testid={`surface-width-${idx}`}
                          />
                        </div>
                        <div className="w-40">
                          <Label className="text-xs">Work Type</Label>
                          <select
                            value={surface.work_type}
                            onChange={(e) => {
                              const updated = [...surfaces];
                              updated[idx].work_type = e.target.value;
                              setSurfaces(updated);
                            }}
                            className="w-full px-2 py-2 border rounded-md text-sm"
                            data-testid={`surface-worktype-${idx}`}
                          >
                            <option value="">Select...</option>
                            <option value="polish">Polish</option>
                            <option value="restore">Restore</option>
                            <option value="seal">Seal & Protect</option>
                            <option value="edge-polish">Edge Polish</option>
                            <option value="full-restoration">Full Restoration</option>
                          </select>
                        </div>
                        {surfaces.length > 1 && (
                          <button
                            type="button"
                            onClick={() => setSurfaces(surfaces.filter((_, i) => i !== idx))}
                            className="p-2 text-red-500 hover:bg-red-50 rounded"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setSurfaces([...surfaces, { name: '', length_mm: '', width_mm: '', work_type: '' }])}
                      className="w-full border-dashed border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                      data-testid="add-surface-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" /> Add Surface
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Stonemasonry - Stone Blocks */}
              {formData.order_type === 'Stonemasonry' && (
                <Card className="mb-6 border-l-4 border-stone-500">
                  <CardHeader>
                    <CardTitle className="text-stone-900">Stone Blocks & Pieces</CardTitle>
                    <p className="text-sm text-stone-600">Dimensions for each block, coping, or piece</p>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {stoneBlocks.map((block, idx) => (
                      <div key={idx} className="flex items-end gap-2 p-3 bg-stone-50 rounded-lg">
                        <div className="flex-1">
                          <Label className="text-xs">Description</Label>
                          <Input
                            value={block.description}
                            onChange={(e) => {
                              const updated = [...stoneBlocks];
                              updated[idx].description = e.target.value;
                              setStoneBlocks(updated);
                            }}
                            placeholder="e.g., Portland stone coping"
                            data-testid={`block-desc-${idx}`}
                          />
                        </div>
                        <div className="w-24">
                          <Label className="text-xs">L (mm)</Label>
                          <Input
                            type="number"
                            value={block.length_mm}
                            onChange={(e) => {
                              const updated = [...stoneBlocks];
                              updated[idx].length_mm = e.target.value;
                              setStoneBlocks(updated);
                            }}
                            placeholder="mm"
                            data-testid={`block-length-${idx}`}
                          />
                        </div>
                        <div className="w-24">
                          <Label className="text-xs">W (mm)</Label>
                          <Input
                            type="number"
                            value={block.width_mm}
                            onChange={(e) => {
                              const updated = [...stoneBlocks];
                              updated[idx].width_mm = e.target.value;
                              setStoneBlocks(updated);
                            }}
                            placeholder="mm"
                            data-testid={`block-width-${idx}`}
                          />
                        </div>
                        <div className="w-24">
                          <Label className="text-xs">D (mm)</Label>
                          <Input
                            type="number"
                            value={block.depth_mm}
                            onChange={(e) => {
                              const updated = [...stoneBlocks];
                              updated[idx].depth_mm = e.target.value;
                              setStoneBlocks(updated);
                            }}
                            placeholder="mm"
                            data-testid={`block-depth-${idx}`}
                          />
                        </div>
                        <div className="w-16">
                          <Label className="text-xs">Qty</Label>
                          <Input
                            type="number"
                            min="1"
                            value={block.quantity}
                            onChange={(e) => {
                              const updated = [...stoneBlocks];
                              updated[idx].quantity = parseInt(e.target.value) || 1;
                              setStoneBlocks(updated);
                            }}
                            data-testid={`block-qty-${idx}`}
                          />
                        </div>
                        {stoneBlocks.length > 1 && (
                          <button
                            type="button"
                            onClick={() => setStoneBlocks(stoneBlocks.filter((_, i) => i !== idx))}
                            className="p-2 text-red-500 hover:bg-red-50 rounded"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setStoneBlocks([...stoneBlocks, { description: '', length_mm: '', width_mm: '', depth_mm: '', quantity: 1 }])}
                      className="w-full border-dashed border-stone-300 text-stone-700 hover:bg-stone-50"
                      data-testid="add-block-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" /> Add Stone Block / Piece
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Line Items - for both Surfacing & Stonemasonry */}
              <Card className="mb-6 border-l-4 border-green-500">
                <CardHeader>
                  <CardTitle className="text-green-900">Quoted Items</CardTitle>
                  <p className="text-sm text-green-600">Add each work item and its price. New lines appear as you type.</p>
                </CardHeader>
                <CardContent className="space-y-3">
                  {lineItems.map((item, idx) => (
                    <div key={idx} className="flex items-end gap-2">
                      <div className="flex-1">
                        {idx === 0 && <Label className="text-xs text-green-800">Description</Label>}
                        <Input
                          value={item.description}
                          onChange={(e) => {
                            const updated = [...lineItems];
                            updated[idx].description = e.target.value;
                            // Auto-add new line when typing in the last item
                            if (idx === lineItems.length - 1 && e.target.value) {
                              updated.push({ description: '', price: '', qty: 1 });
                            }
                            setLineItems(updated);
                          }}
                          placeholder={idx === 0 ? "e.g., Hand carving - 4 hours" : "Add another item..."}
                          data-testid={`line-item-desc-${idx}`}
                        />
                      </div>
                      <div className="w-24">
                        {idx === 0 && <Label className="text-xs text-green-800">Price</Label>}
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">£</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={item.price}
                            onChange={(e) => {
                              const updated = [...lineItems];
                              updated[idx].price = e.target.value;
                              setLineItems(updated);
                            }}
                            placeholder="0.00"
                            className="pl-7"
                            data-testid={`line-item-price-${idx}`}
                          />
                        </div>
                      </div>
                      <div className="w-16">
                        {idx === 0 && <Label className="text-xs text-green-800">Qty</Label>}
                        <Input
                          type="number"
                          min="1"
                          value={item.qty ?? ''}
                          onChange={(e) => {
                            const updated = [...lineItems];
                            updated[idx].qty = e.target.value === '' ? '' : parseInt(e.target.value);
                            setLineItems(updated);
                          }}
                          onBlur={(e) => {
                            if (!e.target.value || parseInt(e.target.value) < 1) {
                              const updated = [...lineItems];
                              updated[idx].qty = 1;
                              setLineItems(updated);
                            }
                          }}
                          className="text-center"
                          data-testid={`line-item-qty-${idx}`}
                        />
                      </div>
                      <div className="w-20 text-right">
                        {idx === 0 && <Label className="text-xs text-green-800">Total</Label>}
                        <div className="h-10 flex items-center justify-end text-sm font-medium text-green-700">
                          £{((parseFloat(item.price) || 0) * (parseInt(item.qty) || 1)).toFixed(2)}
                        </div>
                      </div>
                      {lineItems.length > 1 && item.description && (
                        <button
                          type="button"
                          onClick={() => {
                            const updated = lineItems.filter((_, i) => i !== idx);
                            if (updated.length === 0) updated.push({ description: '', price: '', qty: 1 });
                            setLineItems(updated);
                          }}
                          className="p-2 text-red-500 hover:bg-red-50 rounded"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <div className="flex justify-between items-center pt-2 border-t text-sm">
                    <span className="text-green-700 font-medium">Line Items Subtotal</span>
                    <span className="font-bold text-green-900">
                      £{lineItems.reduce((sum, item) => sum + ((parseFloat(item.price) || 0) * (parseInt(item.qty) || 1)), 0).toFixed(2)}
                    </span>
                  </div>
                  
                  {/* Number of Units for Price Per Unit Calculation */}
                  <div className="mt-4 pt-4 border-t border-green-200">
                    <div className="flex items-end gap-4">
                      <div className="flex-1">
                        <Label className="text-green-800">Number of Units</Label>
                        <p className="text-xs text-green-600 mb-1">e.g., 50 showers - calculates price per unit</p>
                        <Input
                          type="number"
                          min="0"
                          value={formData.number_of_units}
                          onChange={(e) => setFormData({ ...formData, number_of_units: e.target.value })}
                          placeholder="e.g., 50"
                          className="w-32"
                        />
                      </div>
                      {parseInt(formData.number_of_units) > 0 && (
                        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <span className="text-blue-700 text-sm">Price Per Unit:</span>
                          <span className="ml-2 text-blue-900 font-bold text-lg">
                            £{(lineItems.reduce((sum, item) => sum + ((parseFloat(item.price) || 0) * (parseInt(item.qty) || 1)), 0) / parseInt(formData.number_of_units)).toFixed(2)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <>
              {/* Worktop-Specific Fields (Existing) */}
              {/* Multi-Section Toggle */}
              <Card className="mb-6">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base font-semibold">Multi-Section Job</Label>
                      <p className="text-sm text-gray-500">Enable for jobs with multiple stone types/colours in the same project</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={multiSection}
                        onChange={(e) => setMultiSection(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </CardContent>
              </Card>

              {!multiSection ? (
                <>
                  {/* Single Material Mode (Original) */}
                  {/* Stone Details */}
                  <Card className="mb-6">
                    <CardHeader>
                      <CardTitle>Stone Details</CardTitle>
                    </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>Supplier</Label>
                      <Input
                        value={formData.supplier}
                        onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                        placeholder="e.g., Granite Suppliers Ltd"
                      />
                    </div>
                    <div>
                      <Label>Colour Name</Label>
                      <Input
                        value={formData.colour_name}
                        onChange={(e) => setFormData({ ...formData, colour_name: e.target.value })}
                        placeholder="e.g., Black Galaxy"
                      />
                    </div>
                    <div>
                      <Label>Code (£)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.pm2_cost}
                        onChange={(e) => setFormData({ ...formData, pm2_cost: e.target.value })}
                        placeholder="e.g., 250.00"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Pieces */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>Stone Pieces</CardTitle>
                </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b-2 border-gray-300">
                      <th className="text-left py-2 px-2 text-sm font-semibold">#</th>
                      <th className="text-left py-2 px-2 text-sm font-semibold">Name</th>
                      <th className="text-left py-2 px-2 text-sm font-semibold">Width (left to right)</th>
                      <th className="text-left py-2 px-2 text-sm font-semibold">Depth (front to back)</th>
                      <th className="text-left py-2 px-2 text-sm font-semibold">Thickness</th>
                      <th className="text-left py-2 px-2 text-sm font-semibold">Edges</th>
                      <th className="text-left py-2 px-2 text-sm font-semibold">Mitred</th>
                      <th className="text-left py-2 px-2 text-sm font-semibold">Area (m²)</th>
                      <th className="text-left py-2 px-2 text-sm font-semibold">Qty</th>
                      <th className="w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pieces.map((piece, index) => {
                      const areaM2 = piece.width_mm && piece.height_mm 
                        ? ((parseFloat(piece.width_mm) * parseFloat(piece.height_mm)) / 1000000).toFixed(2)
                        : '0.00';
                      
                      return (
                        <tr key={index} className="border-b border-gray-200 hover:bg-gray-50">
                          <td className="py-2 px-2 text-sm text-gray-600">{index + 1}</td>
                          <td className="py-2 px-2">
                            <Input
                              value={piece.name}
                              onChange={(e) => updatePiece(index, 'name', e.target.value)}
                              placeholder="e.g., Upstand, Splash"
                              className="h-8 text-sm"
                            />
                          </td>
                          <td className="py-2 px-2">
                            <Input
                              type="number"
                              value={piece.width_mm}
                              onChange={(e) => updatePiece(index, 'width_mm', e.target.value)}
                              placeholder="3000"
                              className="h-8 text-sm"
                            />
                          </td>
                          <td className="py-2 px-2">
                            <Input
                              type="number"
                              value={piece.height_mm}
                              onChange={(e) => updatePiece(index, 'height_mm', e.target.value)}
                              placeholder="600"
                              className="h-8 text-sm"
                            />
                          </td>
                          <td className="py-2 px-2">
                            <select
                              value={piece.thickness}
                              onChange={(e) => updatePiece(index, 'thickness', e.target.value)}
                              className="w-full h-8 px-2 py-1 border border-gray-300 rounded-md text-sm bg-white"
                            >
                              <option value="20mm">20mm</option>
                              <option value="30mm">30mm</option>
                            </select>
                          </td>
                          <td className="py-2 px-2">
                            <div className="relative" style={{ width: '60px', height: '50px' }}>
                              {/* Top edge */}
                              <button
                                type="button"
                                onClick={() => updatePiece(index, 'edges', { ...piece.edges, top: !piece.edges.top })}
                                style={{ position: 'absolute', top: '0', left: '50%', transform: 'translateX(-50%)', width: '32px', height: '8px' }}
                                className={`${piece.edges.top ? 'bg-blue-500' : 'bg-gray-300'} hover:opacity-80 rounded-sm cursor-pointer border-0`}
                                title="Top edge"
                              />
                              {/* Right edge */}
                              <button
                                type="button"
                                onClick={() => updatePiece(index, 'edges', { ...piece.edges, right: !piece.edges.right })}
                                style={{ position: 'absolute', right: '0', top: '50%', transform: 'translateY(-50%)', width: '8px', height: '32px' }}
                                className={`${piece.edges.right ? 'bg-blue-500' : 'bg-gray-300'} hover:opacity-80 rounded-sm cursor-pointer border-0`}
                                title="Right edge"
                              />
                              {/* Bottom edge */}
                              <button
                                type="button"
                                onClick={() => updatePiece(index, 'edges', { ...piece.edges, bottom: !piece.edges.bottom })}
                                style={{ position: 'absolute', bottom: '0', left: '50%', transform: 'translateX(-50%)', width: '32px', height: '8px' }}
                                className={`${piece.edges.bottom ? 'bg-blue-500' : 'bg-gray-300'} hover:opacity-80 rounded-sm cursor-pointer border-0`}
                                title="Bottom edge"
                              />
                              {/* Left edge */}
                              <button
                                type="button"
                                onClick={() => updatePiece(index, 'edges', { ...piece.edges, left: !piece.edges.left })}
                                style={{ position: 'absolute', left: '0', top: '50%', transform: 'translateY(-50%)', width: '8px', height: '32px' }}
                                className={`${piece.edges.left ? 'bg-blue-500' : 'bg-gray-300'} hover:opacity-80 rounded-sm cursor-pointer border-0`}
                                title="Left edge"
                              />
                              {/* Center rectangle - FACE POLISHING */}
                              <button
                                type="button"
                                onClick={() => updatePiece(index, 'face_polished', !piece.face_polished)}
                                style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '24px', height: '24px' }}
                                className={`${piece.face_polished ? 'bg-blue-500' : 'bg-gray-100'} border ${piece.face_polished ? 'border-blue-600' : 'border-gray-400'} rounded-sm cursor-pointer hover:opacity-80`}
                                title="Face polishing (click to toggle)"
                              />
                            </div>
                          </td>
                          <td className="py-2 px-2 text-center">
                            <input
                              type="checkbox"
                              checked={piece.is_mitred || false}
                              onChange={(e) => updatePiece(index, 'is_mitred', e.target.checked)}
                              className="h-5 w-5 rounded border-gray-300 text-amber-600 focus:ring-amber-500 cursor-pointer"
                              title="Mitred edge (different pricing)"
                            />
                          </td>
                          <td className="py-2 px-2 text-sm font-medium text-gray-700">{areaM2}</td>
                          <td className="py-2 px-2">
                            <Input
                              type="number"
                              min="1"
                              value={piece.quantity ?? ''}
                              onChange={(e) => updatePiece(index, 'quantity', e.target.value === '' ? '' : parseInt(e.target.value))}
                              onBlur={(e) => {
                                if (!e.target.value || parseInt(e.target.value) < 1) {
                                  updatePiece(index, 'quantity', 1);
                                }
                              }}
                              className="w-16 h-8 text-sm text-center"
                              data-testid={`piece-qty-${index}`}
                            />
                          </td>
                          <td className="py-2 px-2">
                            <div className="flex gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => duplicatePiece(index)}
                                className="h-8 w-8 p-0"
                                title="Duplicate piece"
                              >
                                <Plus className="w-4 h-4 text-blue-600" />
                              </Button>
                              {pieces.length > 1 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removePiece(index)}
                                  className="h-8 w-8 p-0"
                                  title="Delete piece"
                                >
                                  <Trash2 className="w-4 h-4 text-red-600" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
                </>
              ) : (
                /* Multi-Section Mode */
                <div className="space-y-6 mb-6">
                  {sections.map((section, sIdx) => (
                    <Card key={section.id} className="border-2 border-blue-200 bg-blue-50/30">
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 flex-1">
                            <span className="bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded">S{sIdx + 1}</span>
                            <Input
                              value={section.name}
                              onChange={(e) => updateSection(sIdx, 'name', e.target.value)}
                              className="font-semibold text-lg h-9 max-w-xs bg-white"
                              placeholder="e.g., Kitchen Island"
                            />
                          </div>
                          {sections.length > 1 && (
                            <Button type="button" variant="ghost" size="sm" onClick={() => removeSection(sIdx)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Section Material (Spec) */}
                        <div>
                          <Label className="text-sm font-semibold text-blue-800 mb-2 block">Spec Material</Label>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div>
                              <Label className="text-xs">Supplier</Label>
                              <Input value={section.supplier} onChange={(e) => updateSection(sIdx, 'supplier', e.target.value)} placeholder="e.g., Quartz" className="h-8 bg-white" />
                            </div>
                            <div>
                              <Label className="text-xs">Colour Name</Label>
                              <Input value={section.colour_name} onChange={(e) => updateSection(sIdx, 'colour_name', e.target.value)} placeholder="e.g., Calacatta Gold" className="h-8 bg-white" />
                            </div>
                            <div>
                              <Label className="text-xs">Code (£/m²)</Label>
                              <Input type="number" step="0.01" value={section.pm2_cost} onChange={(e) => updateSection(sIdx, 'pm2_cost', e.target.value)} placeholder="250.00" className="h-8 bg-white" />
                            </div>
                          </div>
                          {section.pm2_cost && (
                            <p className="text-xs text-blue-700 mt-1 font-medium">
                              Spec cost for this section: £{calculateSectionCost(section).toFixed(2)}
                            </p>
                          )}
                        </div>

                        {/* Section Pieces */}
                        <div>
                          <Label className="text-sm font-semibold text-gray-700 mb-2 block">Pieces</Label>
                          <div className="overflow-x-auto bg-white rounded-md border">
                            <table className="w-full border-collapse">
                              <thead>
                                <tr className="border-b bg-gray-50">
                                  <th className="text-left py-1 px-2 text-xs font-semibold">#</th>
                                  <th className="text-left py-1 px-2 text-xs font-semibold">Name</th>
                                  <th className="text-left py-1 px-2 text-xs font-semibold">Width (mm)</th>
                                  <th className="text-left py-1 px-2 text-xs font-semibold">Depth (mm)</th>
                                  <th className="text-left py-1 px-2 text-xs font-semibold">Thickness</th>
                                  <th className="text-left py-1 px-2 text-xs font-semibold">Edge Profile</th>
                                  <th className="text-left py-1 px-2 text-xs font-semibold">Polished Edges</th>
                                  <th className="text-left py-1 px-2 text-xs font-semibold">Mitred</th>
                                  <th className="text-left py-1 px-2 text-xs font-semibold">m²</th>
                                  <th className="text-left py-1 px-2 text-xs font-semibold">Qty</th>
                                  <th className="w-8"></th>
                                </tr>
                              </thead>
                              <tbody>
                                {section.pieces.map((piece, pIdx) => {
                                  const area = piece.width_mm && piece.height_mm
                                    ? ((parseFloat(piece.width_mm) * parseFloat(piece.height_mm)) / 1000000).toFixed(2)
                                    : '0.00';
                                  return (
                                    <tr key={pIdx} className="border-b hover:bg-gray-50">
                                      <td className="py-1 px-2 text-xs text-gray-500">{pIdx + 1}</td>
                                      <td className="py-1 px-2"><Input value={piece.name} onChange={(e) => updateSectionPiece(sIdx, pIdx, 'name', e.target.value)} placeholder="Piece name" className="h-7 text-xs min-w-[140px]" /></td>
                                      <td className="py-1 px-2"><Input type="number" value={piece.width_mm} onChange={(e) => updateSectionPiece(sIdx, pIdx, 'width_mm', e.target.value)} placeholder="3000" className="h-7 text-xs w-16" /></td>
                                      <td className="py-1 px-2"><Input type="number" value={piece.height_mm} onChange={(e) => updateSectionPiece(sIdx, pIdx, 'height_mm', e.target.value)} placeholder="600" className="h-7 text-xs w-16" /></td>
                                      <td className="py-1 px-2">
                                        <select value={piece.thickness} onChange={(e) => updateSectionPiece(sIdx, pIdx, 'thickness', e.target.value)} className="h-7 px-1 text-xs border rounded bg-white">
                                          <option value="20mm">20mm</option>
                                          <option value="30mm">30mm</option>
                                        </select>
                                      </td>
                                      <td className="py-1 px-2">
                                        <select value={piece.edge_profile || ''} onChange={(e) => updateSectionPiece(sIdx, pIdx, 'edge_profile', e.target.value)} className="h-7 px-1 text-xs border rounded bg-white w-24">
                                          <option value="">None</option>
                                          {settings && Object.entries(settings.edge_profiles || {}).sort((a, b) => a[1] - b[1]).map(([profile, cost]) => (
                                            <option key={profile} value={profile}>{profile} (£{cost}/m)</option>
                                          ))}
                                        </select>
                                      </td>
                                      <td className="py-1 px-2">
                                        <div className="flex gap-1">
                                          {/* L/R/F/B linear-edge picker with per-side mitre cycling.
                                              Each button cycles through 3 states:
                                                 grey   = off (not included)
                                                 blue   = polished (charged at edge_profile rate)
                                                 yellow = mitred  (charged at mitre rate, shows "M" badge)
                                              Backend reads piece.edges + piece.mitred_sides. */}
                                          {[{key:'left',label:'L'},{key:'right',label:'R'},{key:'front',label:'F'},{key:'back',label:'B'}].map(edge => {
                                            const included = !!(piece.edges && piece.edges[edge.key]);
                                            const isMitred = !!(piece.mitred_sides && piece.mitred_sides[edge.key]);
                                            const state = !included ? 'off' : (isMitred ? 'mitred' : 'polished');
                                            const next = state === 'off' ? 'polished' : (state === 'polished' ? 'mitred' : 'off');
                                            const cls = state === 'mitred'
                                              ? 'bg-amber-500 text-white ring-1 ring-amber-700'
                                              : state === 'polished'
                                                ? 'bg-blue-500 text-white'
                                                : 'bg-gray-200 text-gray-600';
                                            return (
                                              <button key={edge.key} type="button"
                                                onClick={() => {
                                                  const newEdges = { ...(piece.edges || {}), [edge.key]: next !== 'off' };
                                                  const newMitred = { ...(piece.mitred_sides || {}), [edge.key]: next === 'mitred' };
                                                  updateSectionPiece(sIdx, pIdx, 'edges', newEdges);
                                                  updateSectionPiece(sIdx, pIdx, 'mitred_sides', newMitred);
                                                }}
                                                className={`w-6 h-5 text-[9px] rounded ${cls}`}
                                                title={
                                                  edge.key === 'back'
                                                    ? `Back edge — currently ${state}. Click to cycle off → polished → mitred.`
                                                    : `${edge.key[0].toUpperCase() + edge.key.slice(1)} edge — currently ${state}. Click to cycle off → polished → mitred.`
                                                }
                                              >{state === 'mitred' ? `${edge.label}*` : edge.label}</button>
                                            );
                                          })}
                                          {[{key:'top',label:'T',title:'Top face (surface polish per m²)'},{key:'bottom',label:'Bt',title:'Bottom face (surface polish per m²)'}].map(edge => (
                                            <button key={edge.key} type="button"
                                              onClick={() => updateSectionPiece(sIdx, pIdx, 'edges', {...piece.edges, [edge.key]: !piece.edges[edge.key]})}
                                              className={`w-6 h-5 text-[9px] rounded ${piece.edges[edge.key] ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'}`}
                                              title={edge.title}
                                            >{edge.label}</button>
                                          ))}
                                        </div>
                                      </td>
                                      <td className="py-1 px-2 text-center">
                                        <input type="checkbox" checked={piece.is_mitred || false} onChange={(e) => updateSectionPiece(sIdx, pIdx, 'is_mitred', e.target.checked)} className="h-4 w-4" />
                                      </td>
                                      <td className="py-1 px-2 text-xs font-medium">{area}</td>
                                      <td className="py-1 px-2"><Input type="number" min="1" value={piece.quantity ?? 1} onChange={(e) => updateSectionPiece(sIdx, pIdx, 'quantity', parseInt(e.target.value) || 1)} className="h-7 text-xs w-12 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" /></td>
                                      <td className="py-1 px-2">
                                        {section.pieces.length > 1 && (
                                          <button type="button" onClick={() => removeSectionPiece(sIdx, pIdx)} className="text-red-400 hover:text-red-600"><X className="w-3 h-3" /></button>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                          <Button type="button" variant="outline" size="sm" onClick={() => addSectionPiece(sIdx)} className="mt-2 text-xs">
                            <Plus className="w-3 h-3 mr-1" /> Add Piece
                          </Button>
                        </div>

                        {/* VE Options */}
                        <div className="border-t pt-3">
                          <Label className="text-sm font-semibold text-amber-700 mb-2 block">VE Options (Value Engineering)</Label>
                          <p className="text-xs text-gray-500 mb-2">Lower-cost alternatives. They show on the quote PDF as an alternative. Tick <strong>"Use this VE for the order"</strong> to apply one to this section — the order total will switch to that VE's pricing.</p>
                          {section.ve_options.map((ve, vIdx) => {
                            const isActive = section.active_ve_id === ve.id;
                            return (
                            <div key={ve.id} className={`mb-3 p-3 rounded border ${isActive ? 'bg-amber-100 border-amber-500 ring-2 ring-amber-400' : 'bg-amber-50 border-amber-200'}`}>
                              <div className="flex items-end gap-2 mb-2">
                                <div className="flex-1">
                                  <Label className="text-xs">Supplier</Label>
                                  <Input value={ve.supplier} onChange={(e) => updateVeOption(sIdx, vIdx, 'supplier', e.target.value)} placeholder="Supplier" className="h-7 text-xs" />
                                </div>
                                <div className="flex-1">
                                  <Label className="text-xs">Colour</Label>
                                  <Input value={ve.colour_name} onChange={(e) => updateVeOption(sIdx, vIdx, 'colour_name', e.target.value)} placeholder="Colour" className="h-7 text-xs" />
                                </div>
                                <div className="w-28">
                                  <Label className="text-xs">£/m²</Label>
                                  <Input type="number" step="0.01" value={ve.pm2_cost} onChange={(e) => updateVeOption(sIdx, vIdx, 'pm2_cost', e.target.value)} placeholder="150" className="h-7 text-xs" />
                                </div>
                                <button type="button" onClick={() => removeVeOption(sIdx, vIdx)} className="text-red-400 hover:text-red-600 pb-1" data-testid={`remove-ve-${sIdx}-${vIdx}`}><X className="w-4 h-4" /></button>
                              </div>
                              <div className="flex items-center gap-4 mt-2 flex-wrap">
                                <label className="flex items-center gap-2 text-xs cursor-pointer font-bold text-amber-900 bg-white px-2 py-1 rounded border border-amber-400" data-testid={`use-ve-toggle-${sIdx}-${vIdx}`}>
                                  <input type="checkbox" checked={isActive} onChange={() => toggleActiveVe(sIdx, ve.id)} className="h-4 w-4" />
                                  <span>Use this VE for the order</span>
                                </label>
                                <label className="flex items-center gap-2 text-xs cursor-pointer">
                                  <input type="checkbox" checked={ve.remove_edge_profiles || false} onChange={(e) => updateVeOption(sIdx, vIdx, 'remove_edge_profiles', e.target.checked)} className="h-3.5 w-3.5" />
                                  <span className="text-amber-800">Revert edges to Bevel (£20/m)</span>
                                </label>
                                <label className="flex items-center gap-2 text-xs cursor-pointer">
                                  <input type="checkbox" checked={ve.remove_cnc || false} onChange={(e) => updateVeOption(sIdx, vIdx, 'remove_cnc', e.target.checked)} className="h-3.5 w-3.5" />
                                  <span className="text-amber-800">Remove CNC work</span>
                                </label>
                                <label className="flex items-center gap-2 text-xs cursor-pointer">
                                  <input type="checkbox" checked={ve.remove_mitres || false} onChange={(e) => updateVeOption(sIdx, vIdx, 'remove_mitres', e.target.checked)} className="h-3.5 w-3.5" />
                                  <span className="text-amber-800">Remove mitres</span>
                                </label>
                              </div>
                              {ve.pm2_cost && (
                                <div className="mt-2 flex items-center justify-between">
                                  <span className="text-xs text-amber-800 font-medium">VE Total: £{calculateVeCost(section, ve).toFixed(2)}</span>
                                  <span className="text-xs text-green-600 font-bold">Save £{(calculateSectionCost(section) - calculateVeCost(section, ve)).toFixed(2)} vs Spec</span>
                                </div>
                              )}
                              {isActive && (
                                <p className="text-xs text-amber-900 font-bold mt-2">✓ This VE is applied to the section — order total reflects this pricing.</p>
                              )}
                            </div>
                            );
                          })}
                          <Button type="button" variant="outline" size="sm" onClick={() => addVeOption(sIdx)} className="text-xs border-amber-300 text-amber-700 hover:bg-amber-50">
                            <Plus className="w-3 h-3 mr-1" /> Add VE Option
                          </Button>
                        </div>

                        {/* CNC Work */}
                        <div className="border-t pt-3">
                          <Label className="text-sm font-semibold text-gray-700 mb-2 block">CNC Work</Label>
                          <div className="flex items-end gap-3">
                            <div className="w-24">
                              <Label className="text-xs">Hours</Label>
                              <Input type="number" step="0.5" min="0" value={section.cnc_hours || ''} onChange={(e) => updateSection(sIdx, 'cnc_hours', e.target.value)} placeholder="0" className="h-8" />
                            </div>
                            <div className="w-28">
                              <Label className="text-xs">Rate (£/hr)</Label>
                              <Input type="number" step="0.01" min="0" value={section.cnc_rate || ''} onChange={(e) => updateSection(sIdx, 'cnc_rate', e.target.value)} placeholder={settings?.cnc_rate || '65'} className="h-8" />
                            </div>
                            {(parseFloat(section.cnc_hours) > 0) && (
                              <p className="text-xs font-medium text-gray-700 pb-2">
                                = £{((parseFloat(section.cnc_hours) || 0) * (parseFloat(section.cnc_rate) || parseFloat(settings?.cnc_rate) || 65)).toFixed(2)}
                              </p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  <Button type="button" variant="outline" onClick={addSection} className="w-full border-dashed border-2 border-blue-300 text-blue-600 hover:bg-blue-50">
                    <Plus className="w-4 h-4 mr-2" /> Add Section
                  </Button>
                </div>
              )}

          {/* Edge Profile - only for single material mode */}
          {!multiSection && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Edge Profile</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Edge Profile</Label>
                  <select
                    value={formData.edge_profile}
                    onChange={(e) => setFormData({ ...formData, edge_profile: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="">None</option>
                    {Object.entries(settings.edge_profiles || {}).sort((a, b) => a[1] - b[1]).map(([profile, cost]) => (
                      <option key={profile} value={profile}>
                        {profile} (£{cost}/m)
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Total Linear Metres (Auto-calculated)</Label>
                  <div className="w-full px-3 py-2 border rounded-md bg-gray-50 font-semibold text-lg">
                    {calculateLinearMetres().totalMetres.toFixed(2)}m
                    <span className="text-sm font-normal text-gray-500 ml-2">
                      ({calculateLinearMetres().polishedMetres.toFixed(2)}m polished, {calculateLinearMetres().mitredMetres.toFixed(2)}m mitred)
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Calculated from selected edges in pieces table</p>
                </div>
              </div>
            </CardContent>
          </Card>
          )}

          {/* Extras */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Extras</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Undermount sink block */}
                <div className={`border rounded-md p-3 ${formData.undermount_sink ? 'bg-slate-50' : ''}`}>
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-2 flex-1">
                      <input
                        type="checkbox"
                        checked={formData.undermount_sink}
                        onChange={(e) => setFormData({ ...formData, undermount_sink: e.target.checked })}
                      />
                      <span className="text-sm font-medium">Undermount Sink Cutout (£{settings.undermount_sink_cost})</span>
                    </label>
                    {formData.undermount_sink && (
                      <input
                        type="number"
                        min="1"
                        value={formData.undermount_sink_qty}
                        onChange={(e) => setFormData({ ...formData, undermount_sink_qty: e.target.value })}
                        className="w-14 h-7 text-sm border rounded px-1"
                        title="Quantity"
                      />
                    )}
                  </div>
                  {formData.undermount_sink && (
                    <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                      <div>
                        <label className="block text-slate-600 mb-1">Width (mm)</label>
                        <input type="number" min="0"
                          value={formData.undermount_sink_width}
                          onChange={(e) => setFormData({ ...formData, undermount_sink_width: e.target.value })}
                          className="w-full h-8 text-sm border rounded px-2"
                          placeholder="e.g. 500"
                          data-testid="undermount-sink-width"
                        />
                      </div>
                      <div>
                        <label className="block text-slate-600 mb-1">Depth (mm)</label>
                        <input type="number" min="0"
                          value={formData.undermount_sink_depth}
                          onChange={(e) => setFormData({ ...formData, undermount_sink_depth: e.target.value })}
                          className="w-full h-8 text-sm border rounded px-2"
                          placeholder="e.g. 400"
                          data-testid="undermount-sink-depth"
                        />
                      </div>
                      <div>
                        <label className="block text-slate-600 mb-1" title="£ reduction per unit off the default">Discount (£/ea)</label>
                        <input type="number" min="0" step="0.01"
                          value={formData.undermount_sink_discount}
                          onChange={(e) => setFormData({ ...formData, undermount_sink_discount: e.target.value })}
                          className="w-full h-8 text-sm border rounded px-2"
                          placeholder="0.00"
                          data-testid="undermount-sink-discount"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Overmount sink block */}
                <div className={`border rounded-md p-3 ${formData.overmount_sink ? 'bg-slate-50' : ''}`}>
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-2 flex-1">
                      <input
                        type="checkbox"
                        checked={formData.overmount_sink}
                        onChange={(e) => setFormData({ ...formData, overmount_sink: e.target.checked })}
                      />
                      <span className="text-sm font-medium">Overmount Sink Cutout (£{settings.overmount_sink_cost})</span>
                    </label>
                    {formData.overmount_sink && (
                      <input
                        type="number"
                        min="1"
                        value={formData.overmount_sink_qty}
                        onChange={(e) => setFormData({ ...formData, overmount_sink_qty: e.target.value })}
                        className="w-14 h-7 text-sm border rounded px-1"
                        title="Quantity"
                      />
                    )}
                  </div>
                  {formData.overmount_sink && (
                    <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                      <div>
                        <label className="block text-slate-600 mb-1">Width (mm)</label>
                        <input type="number" min="0"
                          value={formData.overmount_sink_width}
                          onChange={(e) => setFormData({ ...formData, overmount_sink_width: e.target.value })}
                          className="w-full h-8 text-sm border rounded px-2"
                          placeholder="e.g. 800"
                          data-testid="overmount-sink-width"
                        />
                      </div>
                      <div>
                        <label className="block text-slate-600 mb-1">Depth (mm)</label>
                        <input type="number" min="0"
                          value={formData.overmount_sink_depth}
                          onChange={(e) => setFormData({ ...formData, overmount_sink_depth: e.target.value })}
                          className="w-full h-8 text-sm border rounded px-2"
                          placeholder="e.g. 500"
                          data-testid="overmount-sink-depth"
                        />
                      </div>
                      <div>
                        <label className="block text-slate-600 mb-1" title="£ reduction per unit off the default">Discount (£/ea)</label>
                        <input type="number" min="0" step="0.01"
                          value={formData.overmount_sink_discount}
                          onChange={(e) => setFormData({ ...formData, overmount_sink_discount: e.target.value })}
                          className="w-full h-8 text-sm border rounded px-2"
                          placeholder="0.00"
                          data-testid="overmount-sink-discount"
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.hob_cutout}
                      onChange={(e) => setFormData({ ...formData, hob_cutout: e.target.checked })}
                    />
                    <span className="text-sm">Hob Cut-out (£{settings.hob_cutout_cost})</span>
                  </label>
                  {formData.hob_cutout && (
                    <input
                      type="number"
                      min="1"
                      value={formData.hob_cutout_qty}
                      onChange={(e) => setFormData({ ...formData, hob_cutout_qty: e.target.value })}
                      className="w-14 h-7 text-sm border rounded px-1"
                    />
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.socket_cutout}
                      onChange={(e) => setFormData({ ...formData, socket_cutout: e.target.checked })}
                    />
                    <span className="text-sm">Socket Cut-out (£{settings.socket_cutout_cost})</span>
                  </label>
                  {formData.socket_cutout && (
                    <Input
                      type="number"
                      min="1"
                      value={formData.socket_cutout_qty}
                      onChange={(e) => setFormData({ ...formData, socket_cutout_qty: e.target.value })}
                      className="w-16 h-8 text-sm"
                      placeholder="Qty"
                    />
                  )}
                </div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.popup_socket}
                    onChange={(e) => setFormData({ ...formData, popup_socket: e.target.checked })}
                  />
                  <span className="text-sm">Pop-up Socket (£{settings.popup_socket_cost})</span>
                </label>
                <div className="flex items-center space-x-2">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.tap_hole}
                      onChange={(e) => setFormData({ ...formData, tap_hole: e.target.checked })}
                    />
                    <span className="text-sm">Tap Hole (£{settings.tap_hole_cost})</span>
                  </label>
                  {formData.tap_hole && (
                    <input
                      type="number"
                      min="1"
                      value={formData.tap_hole_qty}
                      onChange={(e) => setFormData({ ...formData, tap_hole_qty: e.target.value })}
                      className="w-14 h-7 text-sm border rounded px-1"
                    />
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.radius_corner}
                      onChange={(e) => setFormData({ ...formData, radius_corner: e.target.checked })}
                    />
                    <span className="text-sm">Radius Corner (£{settings.radius_corner_cost})</span>
                  </label>
                  {formData.radius_corner && (
                    <Input
                      type="number"
                      min="1"
                      value={formData.radius_corner_qty}
                      onChange={(e) => setFormData({ ...formData, radius_corner_qty: e.target.value })}
                      className="w-16 h-8 px-2 text-sm"
                      placeholder="Qty"
                    />
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.large_rounded_corner}
                      onChange={(e) => setFormData({ ...formData, large_rounded_corner: e.target.checked })}
                    />
                    <span className="text-sm">Large Rounded Corner (£{settings.large_rounded_corner_cost})</span>
                  </label>
                  {formData.large_rounded_corner && (
                    <Input
                      type="number"
                      min="1"
                      value={formData.large_rounded_corner_qty}
                      onChange={(e) => setFormData({ ...formData, large_rounded_corner_qty: e.target.value })}
                      className="w-16 h-8 px-2 text-sm"
                      placeholder="Qty"
                    />
                  )}
                </div>
                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.drainer_grooves}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        drainer_grooves: e.target.checked,
                        drainer_grooves_side: e.target.checked ? formData.drainer_grooves_side : '',
                        drainer_grooves_length: e.target.checked ? formData.drainer_grooves_length : '',
                        drainer_sink_depth_mm: e.target.checked ? formData.drainer_sink_depth_mm : '',
                        drainer_groove_count: e.target.checked ? formData.drainer_groove_count : '',
                        drainer_step_distance_mm: e.target.checked ? formData.drainer_step_distance_mm : ''
                      })}
                    />
                    <span className="text-sm">Drainer Grooves (£{settings.drainer_grooves_cost})</span>
                  </label>
                  {formData.drainer_grooves && (
                    <div className="ml-6 mt-2 p-3 bg-slate-50 rounded-lg space-y-4">
                      <div>
                        <label className="text-sm font-medium text-slate-700 block mb-1">
                          Side of Sink (as you look at it) *
                        </label>
                        <div className="flex gap-4">
                          <label className="flex items-center space-x-2">
                            <input
                              type="radio"
                              name="drainer_side"
                              value="left"
                              checked={formData.drainer_grooves_side === 'left'}
                              onChange={(e) => setFormData({ ...formData, drainer_grooves_side: e.target.value })}
                              className="text-blue-600"
                            />
                            <span className="text-sm">Left</span>
                          </label>
                          <label className="flex items-center space-x-2">
                            <input
                              type="radio"
                              name="drainer_side"
                              value="right"
                              checked={formData.drainer_grooves_side === 'right'}
                              onChange={(e) => setFormData({ ...formData, drainer_grooves_side: e.target.value })}
                              className="text-blue-600"
                            />
                            <span className="text-sm">Right</span>
                          </label>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700 block mb-1">
                          Groove Length (mm) - Optional
                        </label>
                        <Input
                          type="number"
                          placeholder="e.g., 400"
                          value={formData.drainer_grooves_length}
                          onChange={(e) => setFormData({ ...formData, drainer_grooves_length: e.target.value })}
                          className="w-32"
                        />
                      </div>
                      
                      {/* Groove Spacing Calculator */}
                      <div className="border-t border-slate-200 pt-4 mt-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Calculator className="h-4 w-4 text-blue-600" />
                          <span className="text-sm font-semibold text-blue-800">Groove Spacing Calculator</span>
                        </div>
                        
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-amber-800">
                              <strong>MANDATORY:</strong> Blade thickness MUST be entered into the saw before cutting.
                              If blade thickness is not set correctly, all spacing calculations are invalid.
                            </p>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Sink Depth (mm) *</Label>
                            <Input
                              type="number"
                              placeholder="e.g., 390"
                              value={formData.drainer_sink_depth_mm}
                              onChange={(e) => {
                                setFormData({ ...formData, drainer_sink_depth_mm: e.target.value });
                                setGrooveCalcResult(null);
                              }}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Blade Width (mm) *</Label>
                            <Input
                              type="number"
                              placeholder="e.g., 10"
                              value={formData.drainer_blade_width_mm}
                              onChange={(e) => {
                                setFormData({ ...formData, drainer_blade_width_mm: e.target.value });
                                setGrooveCalcResult(null);
                              }}
                              className="mt-1"
                            />
                          </div>
                        </div>
                        
                        <div className="mt-3">
                          <Label className="text-xs">Number of Grooves (leave blank for auto-suggest)</Label>
                          <div className="flex gap-2 mt-1">
                            <Input
                              type="number"
                              placeholder="Auto"
                              value={formData.drainer_groove_count}
                              onChange={(e) => {
                                setFormData({ ...formData, drainer_groove_count: e.target.value });
                                setGrooveCalcResult(null);
                              }}
                              className="w-24"
                            />
                            <Button
                              type="button"
                              onClick={calculateGrooveSpacing}
                              disabled={calculatingGrooves || !formData.drainer_sink_depth_mm || !formData.drainer_blade_width_mm}
                              className="bg-blue-600 hover:bg-blue-700"
                            >
                              <Calculator className="h-4 w-4 mr-2" />
                              {calculatingGrooves ? 'Calculating...' : 'Calculate'}
                            </Button>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">
                            Auto: 4 grooves if sink &lt; 340mm, 5 grooves if ≥ 340mm
                          </p>
                        </div>
                        
                        {/* Calculation Results */}
                        {grooveCalcResult && (
                          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                            <div className="grid grid-cols-2 gap-3 mb-3">
                              <div>
                                <p className="text-xs text-slate-600">Number of Grooves</p>
                                <p className="text-lg font-bold text-green-700">
                                  {grooveCalcResult.number_of_grooves}
                                  {grooveCalcResult.is_manual_override && (
                                    <span className="text-xs font-normal text-slate-500 ml-2">(manual)</span>
                                  )}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-600">Step Distance</p>
                                <p className="text-lg font-bold text-green-700">{grooveCalcResult.step_distance_mm} mm</p>
                              </div>
                            </div>
                            <div className="text-xs text-slate-600 border-t border-green-200 pt-2 mt-2">
                              <p className="font-semibold mb-1">Operator Instructions:</p>
                              <ol className="list-decimal list-inside space-y-1">
                                {grooveCalcResult.operator_instructions.map((instruction, idx) => (
                                  <li key={idx} className={instruction.includes('MANDATORY') ? 'text-amber-700 font-semibold' : ''}>
                                    {instruction}
                                  </li>
                                ))}
                              </ol>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.recessed_drainer}
                    onChange={(e) => setFormData({ ...formData, recessed_drainer: e.target.checked })}
                  />
                  <span className="text-sm">Recessed Drainer (£{settings.recessed_drainer_cost})</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.custom_cutout}
                    onChange={(e) => setFormData({ ...formData, custom_cutout: e.target.checked })}
                  />
                  <span className="text-sm">
                    Custom Cut Out (£{settings.custom_cutout_cost_per_m || 25}/m)
                    {formData.custom_cutout && formData.custom_cutout_width && formData.custom_cutout_height && (
                      <span className="ml-2 text-green-600 font-semibold">
                        = £{((parseFloat(formData.custom_cutout_width) + parseFloat(formData.custom_cutout_height)) / 1000 * (settings.custom_cutout_cost_per_m || 25)).toFixed(2)}
                      </span>
                    )}
                  </span>
                </label>
                {formData.custom_cutout && (
                  <div className="ml-6 mt-2 space-y-2 p-3 bg-gray-50 rounded border">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Width (mm)</Label>
                        <Input
                          type="number"
                          placeholder="e.g. 300"
                          value={formData.custom_cutout_width}
                          onChange={(e) => setFormData({ ...formData, custom_cutout_width: e.target.value })}
                          className="h-8 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Depth (mm)</Label>
                        <Input
                          type="number"
                          placeholder="e.g. 400"
                          value={formData.custom_cutout_height}
                          onChange={(e) => setFormData({ ...formData, custom_cutout_height: e.target.value })}
                          className="h-8 text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Location Notes</Label>
                      <Textarea
                        placeholder="e.g. Center of sink run, 100mm from left edge"
                        value={formData.custom_cutout_notes}
                        onChange={(e) => setFormData({ ...formData, custom_cutout_notes: e.target.value })}
                        className="h-16 text-sm"
                      />
                    </div>
                  </div>
                )}

                {/* Custom Extras from Settings */}
                {settings?.custom_extras && settings.custom_extras.length > 0 && (
                  <>
                    <div className="col-span-full border-t pt-3 mt-3">
                      <p className="text-sm font-medium text-gray-700 mb-2">Additional Extras</p>
                    </div>
                    {settings.custom_extras.map((extra) => {
                      const isSelected = formData.selected_custom_extras?.some(e => e.id === extra.id);
                      const selectedExtra = formData.selected_custom_extras?.find(e => e.id === extra.id);
                      
                      return (
                        <div key={extra.id} className="col-span-1">
                          <label className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setFormData({
                                    ...formData,
                                    selected_custom_extras: [
                                      ...(formData.selected_custom_extras || []),
                                      { id: extra.id, name: extra.name, cost: extra.cost, width: '', height: '' }
                                    ]
                                  });
                                } else {
                                  setFormData({
                                    ...formData,
                                    selected_custom_extras: formData.selected_custom_extras.filter(e => e.id !== extra.id)
                                  });
                                }
                              }}
                            />
                            <span className="text-sm">{extra.name} (£{extra.cost})</span>
                          </label>
                          {isSelected && extra.requiresDimensions && (
                            <div className="ml-6 mt-2 flex gap-2">
                              <Input
                                type="number"
                                placeholder="Width mm"
                                value={selectedExtra?.width || ''}
                                onChange={(e) => {
                                  const updated = formData.selected_custom_extras.map(ex =>
                                    ex.id === extra.id ? { ...ex, width: e.target.value } : ex
                                  );
                                  setFormData({ ...formData, selected_custom_extras: updated });
                                }}
                                className="w-24 h-8 text-sm"
                              />
                              <Input
                                type="number"
                                placeholder="Height mm"
                                value={selectedExtra?.height || ''}
                                onChange={(e) => {
                                  const updated = formData.selected_custom_extras.map(ex =>
                                    ex.id === extra.id ? { ...ex, height: e.target.value } : ex
                                  );
                                  setFormData({ ...formData, selected_custom_extras: updated });
                                }}
                                className="w-24 h-8 text-sm"
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Services - Worktop Only */}
          {formData.order_type !== 'Fireplace/Hearth' && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Services</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <label className="flex items-center space-x-2 shrink-0">
                      <input
                        type="checkbox"
                        checked={formData.include_templating}
                        onChange={(e) => setFormData({ ...formData, include_templating: e.target.checked })}
                      />
                      <span>Templating</span>
                    </label>
                    {formData.include_templating && (
                      <>
                        <div className="flex items-center gap-1">
                          <span className="text-sm text-slate-500">£</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={formData.templating_cost_override !== '' ? formData.templating_cost_override : settings.templating_cost}
                            onChange={(e) => setFormData({ ...formData, templating_cost_override: e.target.value })}
                            className="w-24 h-8 text-sm"
                            data-testid="templating-cost-input"
                          />
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-sm text-slate-500">x</span>
                          <Input
                            type="number"
                            min="1"
                            value={formData.templating_qty}
                            onChange={(e) => setFormData({ ...formData, templating_qty: parseInt(e.target.value) || 1 })}
                            className="w-16 h-8 text-sm text-center"
                            data-testid="templating-qty-input"
                          />
                        </div>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center space-x-2 shrink-0">
                      <input
                        type="checkbox"
                        checked={formData.include_fitting}
                        onChange={(e) => setFormData({ ...formData, include_fitting: e.target.checked })}
                      />
                      <span>Fitting</span>
                    </label>
                    {formData.include_fitting && (
                      <>
                        <div className="flex items-center gap-1">
                          <span className="text-sm text-slate-500">£</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={formData.fitting_cost_override !== '' ? formData.fitting_cost_override : settings.fitting_cost}
                            onChange={(e) => setFormData({ ...formData, fitting_cost_override: e.target.value })}
                            className="w-24 h-8 text-sm"
                            data-testid="fitting-cost-input"
                          />
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-sm text-slate-500">x</span>
                          <Input
                            type="number"
                            min="1"
                            value={formData.fitting_qty}
                            onChange={(e) => setFormData({ ...formData, fitting_qty: parseInt(e.target.value) || 1 })}
                            className="w-16 h-8 text-sm text-center"
                            data-testid="fitting-qty-input"
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Delivery Options - For Worktops (Hearths have their own section above) */}
          {formData.order_type !== 'Fireplace/Hearth' && (
            <Card className="mb-6">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="include-delivery-worktop"
                    checked={deliveryMethod === 'delivery'}
                    onChange={(e) => {
                      setDeliveryMethod(e.target.checked ? 'delivery' : 'collection');
                      if (!e.target.checked) {
                        setDeliveryMileage('');
                        setDeliveryCharge('');
                      }
                    }}
                    className="h-5 w-5 rounded border-gray-300"
                  />
                  <Label htmlFor="include-delivery-worktop" className="text-base font-semibold cursor-pointer">Include Delivery</Label>
                </div>
                
                {deliveryMethod === 'delivery' && (
                  <div className="mt-4 pl-8 space-y-3 border-l-2 border-blue-200">
                    <div>
                      <Label>Distance (miles from {basePostcode || 'your base'})</Label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        value={deliveryMileage}
                        onChange={(e) => setDeliveryMileage(e.target.value)}
                        placeholder="e.g., 25"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Rate: £{settings?.delivery_rate_per_mile?.toFixed(2) || '1.50'} per mile
                      </p>
                    </div>
                    <div>
                      <Label>Delivery Charge (£)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={deliveryCharge}
                        onChange={(e) => setDeliveryCharge(e.target.value)}
                        placeholder="Auto-calculated or enter manually"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {deliveryMileage ? `Calculated: ${deliveryMileage} miles × £${settings?.delivery_rate_per_mile?.toFixed(2) || '1.50'}` : 'Enter mileage to auto-calculate, or enter manually'}
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
            </>
          )}

          {/* Pricing */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Pricing</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Custom Fabrication Cost (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.custom_fabrication_cost}
                  onChange={(e) => setFormData({ ...formData, custom_fabrication_cost: e.target.value })}
                  placeholder="Enter custom fabrication cost if needed"
                />
                <p className="text-xs text-gray-500 mt-1">Add any custom fabrication or special work costs here</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Discount (%)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    max="100"
                    value={formData.trade_discount}
                    onChange={(e) => setFormData({ ...formData, trade_discount: e.target.value })}
                    placeholder="e.g. 20"
                  />
                  <p className="text-xs text-gray-500 mt-1">Enter any discount percentage</p>
                </div>
                <div>
                  <Label>VAT Rate (%)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.vat_rate}
                    onChange={(e) => setFormData({ ...formData, vat_rate: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label>Deposit (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.deposit}
                  onChange={(e) => setFormData({ ...formData, deposit: e.target.value })}
                />
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={4}
                placeholder="Add any additional notes or requirements..."
              />
            </CardContent>
          </Card>


          {/* File Attachments */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>File Attachments</CardTitle>
              <p className="text-sm text-gray-600">Upload drawings, plans, or reference images (max 10MB per file)</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div>
                  <input
                    type="file"
                    accept="image/*,.pdf,.dwg,.dxf"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        if (file.size > 10 * 1024 * 1024) {
                          toast.error('File too large. Maximum size is 10MB');
                          return;
                        }
                        
                        // Convert to base64
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          const base64 = event.target.result.split(',')[1];
                          setAttachments([...attachments, {
                            filename: file.name,
                            file_data: base64,
                            size_bytes: file.size
                          }]);
                          toast.success(`${file.name} added`);
                        };
                        reader.readAsDataURL(file);
                        e.target.value = '';
                      }
                    }}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                </div>
                
                {attachments.length > 0 && (
                  <div className="space-y-2">
                    {attachments.map((attachment, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded border">
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span className="text-sm">{attachment.filename}</span>
                          <span className="text-xs text-gray-500">({(attachment.size_bytes / 1024).toFixed(1)} KB)</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setAttachments(attachments.filter((_, i) => i !== index));
                            toast.success('File removed');
                          }}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>


          {/* Price Summary */}
          <Card className="mb-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Quote Summary</span>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-normal flex items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={priceOverride.enabled}
                      onChange={(e) => setPriceOverride({...priceOverride, enabled: e.target.checked})}
                      className="rounded"
                    />
                    Override Total
                  </label>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between text-lg">
                  <span>{formData.trade_discount > 0 ? 'Subtotal (before discount)' : 'Subtotal'}</span>
                  <span className={priceOverride.enabled ? 'line-through text-slate-400' : ''}>
                    £{calculatedPrice.subtotalBeforeDiscount.toFixed(2)}
                  </span>
                </div>
                {formData.trade_discount > 0 && (
                  <>
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Trade Discount ({formData.trade_discount}%)</span>
                      <span>-£{calculatedPrice.discountAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-lg">
                      <span>Subtotal (after discount)</span>
                      <span className={priceOverride.enabled ? 'line-through text-slate-400' : ''}>
                        £{calculatedPrice.subtotal.toFixed(2)}
                      </span>
                    </div>
                  </>
                )}
                <div className="flex justify-between text-lg">
                  <span>VAT ({formData.vat_rate}%)</span>
                  <span className={priceOverride.enabled ? 'line-through text-slate-400' : ''}>
                    £{calculatedPrice.vatAmount.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center text-2xl font-bold pt-2 border-t-2 border-blue-300">
                  <span>Total</span>
                  {priceOverride.enabled ? (
                    <div className="flex items-center gap-2">
                      <span className="text-lg">£</span>
                      <Input
                        type="number"
                        step="0.01"
                        value={priceOverride.total}
                        onChange={(e) => setPriceOverride({...priceOverride, total: e.target.value})}
                        className="w-32 text-right text-xl font-bold"
                        placeholder="0.00"
                      />
                    </div>
                  ) : (
                    <span>£{calculatedPrice.total.toFixed(2)}</span>
                  )}
                </div>
                {priceOverride.enabled && (
                  <div className="text-sm text-amber-600 mt-2 p-2 bg-amber-50 rounded">
                    ⚠️ Manual price override active - calculated price: £{calculatedPrice.total.toFixed(2)}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex gap-3 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/worktops')}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button 
              type="button" 
              variant="secondary"
              onClick={(e) => handleSubmit(e, true)}
              disabled={loading}
              className="bg-blue-100 hover:bg-blue-200 text-blue-700"
            >
              {loading ? 'Saving...' : 'Save as Draft'}
            </Button>
            <Button type="submit" disabled={loading} className="bg-green-600 hover:bg-green-700">
              {loading ? 'Creating...' : 'Create Quote'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateWorktop;